import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import ZminkaInput, { ZminkaText } from "../../components/ZminkaInput.jsx";
import { createPortal } from "react-dom";
import {
  Calendar, CalendarCheck, Plus, X, Trash2, MessageCircle, ListChecks, Search,
  Flame, Equal, ChevronsDown, Repeat, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Send, Pencil, Users as UsersIcon, Archive, ArchiveRestore, FolderKanban,
  Lightbulb, AlertTriangle, Rocket, Columns3, Check, ArrowUpRight, SlidersHorizontal,
  Paperclip, Link2, Download, Loader2, Bold, Italic, List, ListOrdered, Heading2, FileText, Copy, Maximize2, CheckCircle2, Circle, FolderTree,
  ScanSearch, Undo2, Hourglass, Play, Lock, CalendarDays, XCircle, FolderOpen, StickyNote,
  AlertCircle, Dumbbell,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { UDALOST as UDALOST_ZALOZKA } from "../../lib/taby.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// Rozbalovací blok se ukládá jako <details>. Syrové HTML se pouští přes
// sanitizaci, ať poznámka nemůže do stránky propašovat skript.
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import {
  Stranka, EmptyState, Hlaska, AkceZnovu, KartaSeznam, PruhFiltru, FiltrRadek,
  FiltrTlacitko, PlovouciNabidka, Segment, SkeletonRows, Stitek, VyberSeznamu, Zapinatko,
} from "../../components/ui.jsx";
import { ton as tonSloupce } from "../../lib/tony.js";
import { useZamekRolovani } from "../../lib/obrazovka.js";
import { getCurrentUser } from "../../lib/auth.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { toast } from "../../components/Toast.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import PoznamkaPole from "../../components/PoznamkaPole.jsx";
import UkolPicker from "../../components/UkolPicker.jsx";
import UkolHistorie from "../../components/UkolHistorie.jsx";

// Výška pole s názvem úkolu podle obsahu, ať je vidět celý název (hlášení #184).
function prizpusobVysku(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

// === OPERATIONS - Úkoly (redesign 16.7.2026) ===
// Kanban 1:1 dle Notion "Operations - company": Zásobník / Prioritní / Měsíční /
// Týdenní / V procesu + Hotovo a Archiv jako pilulky. Dvě úrovně viditelnosti:
// Management (owner/manager/csm vidí a spravuje vše) a Basic (sales/nutritionist
// vidí jen basic board a hýbou jen se svými úkoly). Backend hlídá obojí.

// === WYSIWYG editor popisu (20.7.2026 v2) ===
// Píšeš a rovnou vidíš formátovaný text (žádné **hvězdičky**). Interně se popis
// dál UKLÁDÁ jako Markdown (kvůli Slacku/Jokymu a stávajícím úkolům) — editor
// si ho na vstupu převede na HTML a při uložení zase zpátky. Podporovaná sada
// odpovídá tlačítkům: tučně, kurzíva, odrážky, číslovaný seznam, nadpis.
const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function mdToHtml(md) {
  const radky = String(md || "").split("\n");
  const out = [];
  let seznam = null; // "ul" | "ol" | null
  const flush = () => { if (seznam) { out.push(`</${seznam}>`); seznam = null; } };
  const inline = (s) => escapeHtml(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  for (const r of radky) {
    if (/^- /.test(r)) { if (seznam !== "ul") { flush(); out.push("<ul>"); seznam = "ul"; } out.push(`<li>${inline(r.slice(2))}</li>`); continue; }
    if (/^\d+\. /.test(r)) { if (seznam !== "ol") { flush(); out.push("<ol>"); seznam = "ol"; } out.push(`<li>${inline(r.replace(/^\d+\. /, ""))}</li>`); continue; }
    flush();
    if (/^#{1,3} /.test(r)) { out.push(`<h2>${inline(r.replace(/^#{1,3} /, ""))}</h2>`); continue; }
    if (r.trim() === "") { out.push("<p><br></p>"); continue; }
    out.push(`<p>${inline(r)}</p>`);
  }
  flush();
  return out.join("");
}
function htmlToMd(node) {
  // Rekurzivní převod omezené HTML sady zpět na Markdown.
  const inline = (n) => {
    let s = "";
    for (const ch of n.childNodes) {
      if (ch.nodeType === 3) s += ch.textContent;
      else if (ch.nodeName === "BR") s += "\n";
      else if (ch.nodeName === "STRONG" || ch.nodeName === "B") { const t = inline(ch).trim(); s += t ? `**${t}**` : ""; }
      else if (ch.nodeName === "EM" || ch.nodeName === "I") { const t = inline(ch).trim(); s += t ? `*${t}*` : ""; }
      else if (ch.style && (ch.style.fontWeight === "bold" || parseInt(ch.style.fontWeight, 10) >= 600)) { const t = inline(ch).trim(); s += t ? `**${t}**` : ""; }
      else if (ch.style && ch.style.fontStyle === "italic") { const t = inline(ch).trim(); s += t ? `*${t}*` : ""; }
      else if (ch.nodeName === "IMG") { s += (ch.getAttribute("alt") || ch.getAttribute("aria-label") || ""); } // Slack/Word emoji jako <img alt="😀"> — zachovat
      else s += inline(ch);
    }
    return s;
  };
  const bloky = [];
  const walk = (n) => {
    for (const ch of n.childNodes) {
      const jm = ch.nodeName;
      if (jm === "UL") bloky.push([...ch.querySelectorAll(":scope > li")].map((li) => `- ${inline(li).trim()}`).join("\n"));
      else if (jm === "OL") bloky.push([...ch.querySelectorAll(":scope > li")].map((li, i) => `${i + 1}. ${inline(li).trim()}`).join("\n"));
      else if (/^H[1-6]$/.test(jm)) bloky.push(`## ${inline(ch).trim()}`);
      else if (jm === "P" || jm === "DIV") {
        // Prohlížeč umí zabalit seznam do odstavce (<p><ul>…</ul></p>). Kdyby
        // se takový blok bral jako obyčejný text, položky by se slily do jedné
        // věty bez mezer, tak se do něj zanoříme.
        if (ch.querySelector("ul, ol, p, div, h1, h2, h3, h4, h5, h6")) { walk(ch); continue; }
        const t = inline(ch).replace(/\n+$/, "");
        bloky.push(t.trim() === "" ? "" : t);
      }
      else if (ch.nodeType === 3) { if (ch.textContent.trim()) bloky.push(ch.textContent); }
      else walk(ch);
    }
  };
  walk(node);
  // Bloky odděl prázdným řádkem, víc prázdných po sobě srazit na jeden.
  return bloky.join("\n\n").replace(/\n{3,}/g, "\n\n").trim();
}
export function PopisEditor({ hodnota, onSave, onClose, autoFocus = false }) {
  const ref = useRef(null);
  const vychoziHtml = useMemo(() => mdToHtml(hodnota || ""), []); // eslint-disable-line react-hooks/exhaustive-deps
  const cmd = (prikaz, arg = null) => {
    ref.current?.focus();
    document.execCommand(prikaz, false, arg);
  };
  const Btn = ({ Icon, title, onClick }) => (
    <button type="button" title={title} onMouseDown={(ev) => ev.preventDefault()} onClick={onClick}
      className="p-1.5 rounded-xl text-slate-500 hover:text-violet-700 hover:bg-violet-50 transition-colors">
      <Icon className="w-4 h-4" />
    </button>
  );
  return (
    <div>
      <div className="flex items-center gap-0.5 bg-slate-50 border border-slate-200 rounded-t-xl px-1.5 py-1">
        <Btn Icon={Bold} title="Tučně" onClick={() => cmd("bold")} />
        <Btn Icon={Italic} title="Kurzíva" onClick={() => cmd("italic")} />
        <span className="w-px h-4 bg-slate-200 mx-1" />
        <Btn Icon={List} title="Odrážky" onClick={() => cmd("insertUnorderedList")} />
        <Btn Icon={ListOrdered} title="Číslovaný seznam" onClick={() => cmd("insertOrderedList")} />
        <Btn Icon={Heading2} title="Nadpis" onClick={() => cmd("formatBlock", "<h2>")} />
        <span className="flex-1" />
        <span className="text-[11px] text-slate-400 pr-1.5 hidden sm:block">piš rovnou, formátuje se hned, uloží se samo</span>
      </div>
      <div
        ref={(el) => {
          if (el && !ref.current) {
            ref.current = el;
            el.innerHTML = vychoziHtml || "<p><br></p>";
            if (autoFocus) el.focus();
          }
        }}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Doplň detaily úkolu…"
        onPaste={(e) => {
          // Vložení jen jako čistý text — ať se sem netahá cizí formátování z webů/Wordu.
          e.preventDefault();
          document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
        }}
        onBlur={() => {
          const md = htmlToMd(ref.current);
          if (md !== String(hodnota || "").trim()) onSave(md);
          onClose?.();
        }}
        className="w-full text-sm text-slate-700 bg-white border border-slate-200 border-t-0 rounded-b-xl px-3 py-2.5 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-violet-200 transition-colors
          prose prose-sm max-w-none prose-slate prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-slate-800"
      />
    </div>
  );
}

const COLUMNS = [
  // Stav 'odlozeno' tu schvalne NENI: odlozene ukoly patri do sekce Odlozeno
  // uvnitr meetingu, ne do boardu (18.8.2026). Backlog jsou napady na pozdeji.
  // 4.9.2026 (fáze 4a): `ikona` a `ton` nahradily emoji a šest ručních barev.
  // Tóny jsou z src/lib/tony.js, takže sloupec vypadá stejně v boardu i ve výběru.
  { key: "zasobnik", label: "Backlog", ikona: Lightbulb, ton: "neutralni", hint: "nápady na později" },
  { key: "prioritni", label: "Prioritní", ikona: AlertTriangle, ton: "problem", hint: "do 12 měsíců" },
  { key: "mesicni", label: "Měsíční", ikona: Calendar, ton: "info" },
  { key: "tydenni", label: "Týdenní", ikona: CalendarCheck, ton: "znacka" },
  { key: "v_procesu", label: "V procesu", ikona: Rocket, ton: "ok" },
];
const PINNED_GROUPS = [
  { key: "hotovo", label: "Hotovo", ikona: CheckCircle2, ton: "ok" },
  { key: "archiv", label: "Archiv", ikona: Archive, ton: "neutralni" },
];
const STATUS_LABELS = Object.fromEntries([...COLUMNS, ...PINNED_GROUPS].map((c) => [c.key, c.label]));
// Priorita se v seznamech kreslí štítkem, tón bere ze vzorníku.
const PRIORITA_TON = { High: "problem", Medium: "pozor", Low: "neutralni" };

// 17.7.2026: TOP zrušeno (migrace na High), místo něj štítek `kontrola`.
const PRIORITY_META = {
  High: { label: "Vysoká", border: "#DC2626", bg: "bg-red-50", text: "text-red-800", icon: Flame },
  Medium: { label: "Střední", border: "#D97706", bg: "bg-amber-50", text: "text-amber-800", icon: Equal },
  Low: { label: "Nízká", border: "#64748B", bg: "bg-slate-100", text: "text-slate-700", icon: ChevronsDown },
};
const PRIO_ORDER = { high: 1, medium: 2, low: 3 };

const KATEGORIE_BARVA = {
  Marketing: "bg-yellow-100 text-yellow-800",
  Operations: "bg-pink-100 text-pink-800",
  HR: "bg-amber-100 text-amber-900",
  "Clients Management": "bg-blue-100 text-blue-800",
  System: "bg-red-100 text-red-800",
};

function todayIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function deadlineLabel(iso, today) {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { text: iso, tone: "neutral" };
  if (iso < today) {
    const days = Math.round((new Date(today + "T00:00:00") - new Date(iso + "T00:00:00")) / 86400000);
    return { text: `${days} d po termínu`, tone: "overdue" };
  }
  if (iso === today) return { text: "Dnes", tone: "today" };
  return { text: `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}.`, tone: "future" };
}
// Tón termínu ve štítku (vzorník).
const DEADLINE_TON = { overdue: "problem", today: "pozor", future: "neutralni", neutral: "neutralni" };

// 4.9.2026 (fáze 4a): pole a tlačítka jsou třídy z návrhového systému
// (src/index.css), ne vlastní kopie. Vzhled i dotykové rozměry pak drží s appkou.
const btnGhost = "btn btn-ghost";

// === Přílohy: optimalizace obrázků v prohlížeči (kvůli úložišti) ===
// JPEG/PNG/WebP se zmenší na max 1600 px a překóduje do JPEG ~82 % kvality.
// Malé obrázky a ostatní typy souborů se nechají beze změny.
async function optimalizujObrazek(file) {
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return file;
  if (file.size < 300 * 1024) return file; // malé není třeba řešit
  try {
    const img = await createImageBitmap(file);
    const MAX = 1600;
    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    if (blob && blob.size < file.size) {
      return new File([blob], file.name.replace(/\.(png|webp|jpeg|jpg)$/i, "") + ".jpg", { type: "image/jpeg" });
    }
  } catch { /* fallback: původní soubor */ }
  return file;
}
function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}
const fmtVelikost = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);
// Autor přílohy (e-mail) → čitelné jméno pro zobrazení.
const prettyAutor = (email) => {
  const local = String(email || "").split("@")[0].replace(/[._-]+/g, " ").trim();
  return local ? local.replace(/\b\w/g, (c) => c.toUpperCase()) : (email || "—");
};
const fmtDatum = (iso) => { try { return new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };
// Emoji do textových poznámek.
/** Povolené značky v poznámce: běžný Markdown plus rozbalovací blok. */
const SANITIZE = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "details", "summary"],
  attributes: { ...defaultSchema.attributes, details: ["open"] },
};

// Převod markdownu poznámky do formátu dané platformy pro kopírování. Emoji zůstávají.
/* --- kopírování poznámky jinam ------------------------------------------------
 *
 * Poznámka umí víc úrovní odrážek a rozbalovací bloky, chaty ani mail nic
 * takového neznají. Zanoření se proto vysází odsazením a jinou značkou,
 * rozbalovací blok se zploští na tučný nadpis a obsah pod ním. Nic z textu
 * se neztrácí. Do mailu jde navíc HTML, takže tam odrážky zůstanou odrážkami.
 */
const odsad = (radek) => /^[ \t]*/.exec(radek)[0].replace(/\t/g, "    ").length;
const ZNACKY = ["•", "◦", "▪"];

function rozbalToggly(md) {
  return String(md || "")
    .replace(/^[ \t]*<details(?:\s[^>]*)?>[ \t]*$/gim, "")
    .replace(/^[ \t]*<\/details>[ \t]*$/gim, "")
    .replace(/^[ \t]*<summary>([\s\S]*?)<\/summary>[ \t]*$/gim, (_, t) => `**${String(t).trim()}**`);
}

/** Úroveň zanoření podle odsazení, počítá se od nejmělčí položky seznamu. */
function urovneSeznamu(radky) {
  const stack = [];
  return radky.map((r) => {
    if (!/^[ \t]*([-*+]|\d+[.)])\s+/.test(r)) { stack.length = 0; return 0; }
    const o = odsad(r);
    while (stack.length && o < stack[stack.length - 1]) stack.pop();
    if (!stack.length || o > stack[stack.length - 1]) stack.push(o);
    return Math.min(stack.length - 1, ZNACKY.length - 1);
  });
}

function mdToPlatform(md, platform) {
  const tucne = platform === "whatsapp" || platform === "slack" ? "*" : platform === "telegram" ? "" : "";
  const kurziva = platform === "whatsapp" || platform === "slack" ? "_" : "";
  const radky = rozbalToggly(md).split("\n");
  const uroven = urovneSeznamu(radky);
  return radky
    .map((radek, i) => {
      // Tučné se odloží stranou, jinak by ho druhý průchod přepsal na kurzívu.
      let r = radek
        .replace(/^\s{0,3}#{1,6}\s+/, "")
        .replace(/\*\*([^*\n]+)\*\*/g, (_, x) => `\u0000${x}\u0000`)
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, (_, pre, x) => `${pre}${kurziva}${x}${kurziva}`)
        .replace(/\u0000([^\u0000]+)\u0000/g, (_, x) => `${tucne}${x}${tucne}`);
      const u = uroven[i];
      const cislo = /^[ \t]*(\d+)[.)]\s+/.exec(r);
      if (cislo) return `${"   ".repeat(u)}${cislo[1]}. ${r.replace(/^[ \t]*\d+[.)]\s+/, "")}`;
      if (/^[ \t]*[-*+]\s+/.test(r)) return `${"   ".repeat(u)}${ZNACKY[u]} ${r.replace(/^[ \t]*[-*+]\s+/, "")}`;
      return r.replace(/\s+$/, "");
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Markdown na HTML pro mail. Vnořený seznam patří dovnitř nadřazené položky. */
function mdToHtmlMail(md) {
  const escape = (x) => String(x).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (x) => escape(x)
    .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  const out = [];
  const stack = [];
  let odstavec = [];
  const zavriOdstavec = () => {
    if (!odstavec.length) return;
    const h = /^(#{1,6})\s+(.*)$/.exec(odstavec[0]);
    if (h && odstavec.length === 1) out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`);
    else out.push(`<p>${odstavec.map(inline).join("<br>")}</p>`);
    odstavec = [];
  };
  const zavriSeznam = () => {
    const s = stack.pop();
    if (s.polozkaOtevrena) out.push("</li>");
    out.push(`</${s.typ}>`);
    if (stack.length) stack[stack.length - 1].polozkaOtevrena = true;
  };
  const zavriVse = () => { while (stack.length) zavriSeznam(); };

  for (const radek of rozbalToggly(md).split("\n")) {
    if (!radek.trim()) { zavriOdstavec(); zavriVse(); continue; }
    const b = /^([ \t]*)([-*+]|\d+[.)])\s+(.*)$/.exec(radek);
    if (!b) { zavriVse(); odstavec.push(radek); continue; }
    zavriOdstavec();
    const o = odsad(b[1]);
    const typ = /\d/.test(b[2]) ? "ol" : "ul";
    while (stack.length && o < stack[stack.length - 1].odsad) zavriSeznam();
    if (!stack.length) { stack.push({ typ, odsad: o, polozkaOtevrena: false }); out.push(`<${typ}>`); }
    else if (o > stack[stack.length - 1].odsad) {
      stack[stack.length - 1].polozkaOtevrena = false;
      stack.push({ typ, odsad: o, polozkaOtevrena: false });
      out.push(`<${typ}>`);
    } else {
      const vrchol = stack[stack.length - 1];
      if (vrchol.polozkaOtevrena) { out.push("</li>"); vrchol.polozkaOtevrena = false; }
      if (vrchol.typ !== typ) { out.push(`</${vrchol.typ}>`); stack.pop(); stack.push({ typ, odsad: o, polozkaOtevrena: false }); out.push(`<${typ}>`); }
    }
    out.push(`<li>${inline(b[3])}`);
    stack[stack.length - 1].polozkaOtevrena = true;
  }
  zavriOdstavec();
  zavriVse();
  return out.join("");
}

/**
 * Do schránky jdou obě podoby najednou: mail si vezme HTML a odrážky
 * zůstanou odrážkami, chat si vezme text. Když prohlížeč `ClipboardItem`
 * neumí, pošle se aspoň text.
 */
export async function zkopirujPoznamku(md, platform) {
  const text = mdToPlatform(md, platform);
  if ((platform === "plain" || platform === "email") && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/html": new Blob([mdToHtmlMail(md)], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      })]);
      return true;
    } catch (_) { /* spadneme na text */ }
  }
  await navigator.clipboard.writeText(text);
  return true;
}

// Náhled obrázkové přílohy — stahuje se přes apiFetch (auth hlavičky), takže
// nejde použít <img src>; blob se načte do objectURL.
function PrilohaThumb({ blobKey }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    let zij = true;
    apiFetch(`/api/operations-ukoly-file?key=${encodeURIComponent(blobKey)}`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && zij) { url = URL.createObjectURL(b); setSrc(url); } })
      .catch(() => {});
    return () => { zij = false; if (url) URL.revokeObjectURL(url); };
  }, [blobKey]);
  if (!src) return <span className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Loader2 className="w-3.5 h-3.5 animate-spin text-slate-300" /></span>;
  return <img src={src} alt="" className="w-9 h-9 rounded-xl object-cover shrink-0 border border-slate-200" />;
}

// Systémové tagy — nezobrazují se jako běžné štítky (mají vlastní badge/zdroj).
const SYSTEM_TAGS = new Set(["rutina", "ukazka", "slack"]);
const splitTags = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

// Avatar s fotkou z HR evidence, fallback na iniciály (stejně jako Time tracking).
function Avatar({ name, foto, className = "w-5 h-5", textClassName = "text-[11px]" }) {
  if (!name) return null;
  if (foto) return <img src={foto} alt={name} className={`${className} rounded-full object-cover shrink-0 demo-sensitive`} />;
  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span className={`${className} rounded-full bg-gradient-to-br from-violet-400 to-violet-600 text-white ${textClassName} font-bold flex items-center justify-center shrink-0 demo-sensitive`}>
      {initials}
    </span>
  );
}

function PilotBadge({ name, foto }) {
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <Avatar name={name} foto={foto} />
      <span className="text-xs text-slate-600 truncate demo-sensitive">{name}</span>
    </span>
  );
}

// Chytřejší tagy: chipy s odebráním křížkem, přidání Enterem nebo kliknutím
// na existující tag z boardu (napovídání). Ukládá se pořád CSV do `tags`.
function TagInput({ value, suggestions, onSave }) {
  const [input, setInput] = useState("");
  const tags = splitTags(value);
  const visible = tags.filter((t) => !SYSTEM_TAGS.has(t.toLowerCase()));
  const system = tags.filter((t) => SYSTEM_TAGS.has(t.toLowerCase()));
  const save = (arr) => onSave([...system, ...arr].join(", "));
  const add = (t) => {
    const v = String(t || "").trim().replace(/,/g, "");
    if (!v || visible.some((x) => x.toLowerCase() === v.toLowerCase())) { setInput(""); return; }
    save([...visible, v]);
    setInput("");
  };
  const napoveda = (suggestions || [])
    .filter((s) => !SYSTEM_TAGS.has(s.toLowerCase()) && !visible.some((x) => x.toLowerCase() === s.toLowerCase()))
    .filter((s) => !input.trim() || s.toLowerCase().includes(input.trim().toLowerCase()))
    .slice(0, 6);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 border border-slate-300 rounded-xl px-2 py-1.5 bg-white focus-within:ring-2 focus-within:ring-violet-300">
        {visible.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold bg-violet-50 text-violet-800 border border-violet-200 rounded-full pl-2 pr-1 py-0.5">
            #{t}
            <button type="button" onClick={() => save(visible.filter((x) => x !== t))} className="p-0.5 rounded-full hover:bg-violet-100"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[90px] text-sm py-0.5 focus:outline-none"
          placeholder={visible.length ? "" : "Přidat tag…"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
            else if (e.key === "Backspace" && !input && visible.length) save(visible.slice(0, -1));
          }}
          onBlur={() => input.trim() && add(input)}
        />
      </div>
      {napoveda.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {napoveda.map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 4.9.2026 (fáze 4a): karta úkolu ve vzorníku — štítky mají jednotné tóny,
// projekt pozná člověk podle ikony a proužku značky, ne podle druhé barvy.
function TaskCard({ task, onOpen, today, foto }) {
  const prio = PRIORITY_META[task.priority] || PRIORITY_META.Medium;
  const dl = deadlineLabel(task.deadline, today);
  const subPct = task.subtasks_total > 0 ? Math.round((task.subtasks_done / task.subtasks_total) * 100) : null;
  // Projekt = master úkol sdružující další úkoly (explicitně nebo má navázané).
  const jeProjekt = task.je_projekt === true || (task.deti_total || 0) > 0;
  const projPct = (task.deti_total || 0) > 0 ? Math.round((task.deti_done / task.deti_total) * 100) : 0;
  const prouzek = { High: "bg-rose-500", Medium: "bg-amber-500", Low: "bg-slate-300" }[task.priority] || "bg-amber-500";
  return (
    <button
      type="button"
      onClick={() => onOpen(task.id)}
      className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-glass"
    >
      <div className="flex">
        <div className={`w-1.5 shrink-0 ${jeProjekt ? "bg-brand" : prouzek}`} aria-hidden="true" />
        <div className="min-w-0 flex-1 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 text-sm font-semibold leading-snug text-slate-900">
              {jeProjekt && <FolderKanban className="mr-1 -mt-0.5 inline h-3.5 w-3.5 text-brand" />}
              {task.nazev}
            </div>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {task.ke_kontrole_kdy
              ? <Stitek velikost="mala" ton="pozor">ke kontrole</Stitek>
              : task.kontrola && <Stitek velikost="mala" ton="pozor">kontrola</Stitek>}
            {task.uroven === "basic" && <Stitek velikost="mala" ton="info">basic</Stitek>}
            <Stitek velikost="mala" ton={PRIORITA_TON[task.priority] || "neutralni"} ikona={prio.icon}>{prio.label}</Stitek>
            {task.kategorie && <Stitek velikost="mala" ton="neutralni">{task.kategorie}</Stitek>}
            {dl && <Stitek velikost="mala" ton={DEADLINE_TON[dl.tone] || "neutralni"} ikona={Calendar}>{dl.text}</Stitek>}
            {task.rutina_id && <Stitek velikost="mala" ton="info" ikona={Repeat}>rutina</Stitek>}
            {task.parent_id && <Stitek velikost="mala" ton="znacka">v projektu</Stitek>}
            {splitTags(task.tags).filter((t) => !SYSTEM_TAGS.has(t.toLowerCase())).slice(0, 2).map((t) => (
              <Stitek key={t} velikost="mala" ton="neutralni">#{t}</Stitek>
            ))}
          </div>
          {/* Projekt: plnění navázaných úkolů přímo na kartě */}
          {(task.deti_total || 0) > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-brand-deep">
              <FolderKanban className="h-3 w-3 shrink-0" /> {task.deti_done}/{task.deti_total} úkolů
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-brand-100">
                <span className="block h-full bg-brand" style={{ width: `${projPct}%` }} />
              </span>
              <span className="tabular-nums">{projPct} %</span>
            </div>
          )}
          {subPct != null && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
              <ListChecks className="h-3 w-3 shrink-0" /> {task.subtasks_done}/{task.subtasks_total}
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <span className="block h-full bg-emerald-400" style={{ width: `${subPct}%` }} />
              </span>
            </div>
          )}
          {task.pilot && <div className="mt-2"><PilotBadge name={task.pilot} foto={foto} /></div>}
        </div>
      </div>
    </button>
  );
}

// Modal detail úkolu (redesign 17.7.2026): barevná hlavička dle sloupce, velký editovatelný
// název, obsah vlevo (popis, podúkoly s progresem, komentáře jako bublinky s avatary),
// vlastnosti v pravém panelu. Editace polí ukládá na blur/změnu jako dřív.
// Export: detail úkolu se otevírá i z meetingu (sekce Úkoly z minulého týdne).
// Editor textové poznámky: název + plné formátování (jako popis úkolu) + emoji.
// Koncept rozepsané poznámky žije v prohlížeči, ne na serveru (2.10.2026, zadání
// Ládi). Důvod: text se ztrácel, když vypadla síť při ukládání nebo když člověk
// klepl vedle okna. Klíč je per poznámka, ať se dvě rozepsané nepřetlučou.
export const konceptKlic = (draftKey) => (draftKey ? `fw40_poznamka_koncept_${draftKey}` : "");
export function smazKoncept(draftKey) {
  const k = konceptKlic(draftKey);
  if (!k) return;
  try { localStorage.removeItem(k); } catch (_) { /* privátní okno */ }
}
const casZKonceptu = (ms) => {
  try { return new Date(ms).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" }); }
  catch (_) { return ""; }
};
// Název se doplní z prvního řádku textu, ať Uložit nikdy neblokuje prázdné pole.
const nazevZTextu = (obsah) => {
  const radek = String(obsah || "").split("\n").map((r) => r.replace(/^[#>\-*\s]+/, "").trim()).find(Boolean) || "";
  return radek.slice(0, 80);
};

export function TextPrilohaEditor({ initialNazev = "", initialObsah = "", onSave, onClose, saving = false, inline = false, draftKey = "", chyba = "" }) {
  const [nazev, setNazev] = useState(initialNazev);
  const [obsah, setObsah] = useState(initialObsah || "");
  const [koncept, setKoncept] = useState(null);     // nalezený koncept z minula
  const [konceptAt, setKonceptAt] = useState("");   // čas posledního odložení
  const puvodni = useRef({ nazev: initialNazev, obsah: initialObsah || "" });
  const klic = konceptKlic(draftKey);
  const zmeneno = nazev !== puvodni.current.nazev || obsah !== puvodni.current.obsah;

  // Při otevření nabídneme koncept, nepřepisujeme ho rovnou: co je na serveru,
  // může být novější a člověk musí vědět, co si přetahuje zpátky.
  useEffect(() => {
    if (!klic) return;
    try {
      const raw = localStorage.getItem(klic);
      if (!raw) return;
      const d = JSON.parse(raw);
      const stejny = String(d.nazev || "") === String(initialNazev || "") && String(d.obsah || "") === String(initialObsah || "");
      if (!stejny && (String(d.obsah || "").trim() || String(d.nazev || "").trim())) setKoncept(d);
    } catch (_) { /* nic, koncept je jen pojistka */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klic]);

  // Odkládání konceptu: sekundu po posledním doťuknutí, ne při každém písmenu.
  useEffect(() => {
    if (!klic || !zmeneno) return undefined;
    const t = setTimeout(() => {
      try {
        const at = Date.now();
        localStorage.setItem(klic, JSON.stringify({ nazev, obsah, at }));
        setKonceptAt(casZKonceptu(at));
      } catch (_) { /* plné nebo vypnuté úložiště, text zůstává v poli */ }
    }, 900);
    return () => clearTimeout(t);
  }, [nazev, obsah, klic, zmeneno]);

  const ulozit = () => {
    if (!obsah.trim()) return;
    onSave((nazev.trim() || nazevZTextu(obsah) || "Poznámka"), obsah.trim());
  };

  // Křížek i klepnutí vedle okna se u rozepsaného textu ptají. Koncept zůstává tak jako tak.
  const zavrit = async () => {
    if (zmeneno) {
      const ano = await confirmDialog({
        title: "Zavřít poznámku?",
        description: "Rozepsaný text zůstane uložený jako koncept a nabídne se ti při dalším otevření.",
        confirmLabel: "Zavřít",
      });
      if (!ano) return;
    }
    onClose();
  };

  // Overlay jde portalem do document.body (13.8.2026): detail ukolu je sam
  // overlay s transformem a "fixed" uvnitr nej se pocital od nej — nahled
  // poznamky pak visel jen pres cast stranky.
  const telo = (
    <div className={inline ? "" : "fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto p-3 sm:p-6"} onClick={inline ? undefined : (e) => { e.stopPropagation(); zavrit(); }}>
      <div className={inline ? "bg-white rounded-2xl border border-violet-200 shadow-sm w-full" : "bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4"} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-slate-800 font-bold"><FileText className="w-4 h-4 text-violet-500" /> Poznámka</div>
          <button onClick={zavrit} className="p-2 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0"><X className="w-5 h-5" /></button>
        </div>
        {koncept && (
          <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
            <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
            <span className="text-[13px] font-semibold text-amber-900">
              Máš tu rozepsaný koncept{koncept.at ? ` z ${casZKonceptu(koncept.at)}` : ""}
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <button type="button"
                onClick={() => { setNazev(koncept.nazev || ""); setObsah(koncept.obsah || ""); setKoncept(null); }}
                className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100">Obnovit</button>
              <button type="button"
                onClick={() => { smazKoncept(draftKey); setKoncept(null); }}
                className="rounded-full px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100">Zahodit</button>
            </span>
          </div>
        )}
        <div className="p-4 space-y-3">
          <input autoFocus value={nazev} onChange={(e) => setNazev(e.target.value)} maxLength={150}
            placeholder="Název (doplní se z prvního řádku)" className="field" />
          {/* Stejný editor jako v pm-app: odrážky, číslování a odškrtávátka
              na víc úrovní (Tab zanoří) a rozbalovací bloky. Ukládá Markdown. */}
          <PoznamkaPole value={obsah} onChange={setObsah} minHeight="14rem"
            placeholder="Piš rovnou, formátování zvládne lišta nahoře." />
        </div>
        {chyba && (
          <div className="mx-4 mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
            <span className="text-[13px] font-semibold text-rose-900">Uložení se nepovedlo, text máš uložený v konceptu</span>
            <button type="button" onClick={ulozit} disabled={saving}
              className="ml-auto rounded-full border border-rose-300 bg-white px-3 py-1 text-xs font-bold text-rose-700 hover:bg-rose-100">Zkusit znovu</button>
          </div>
        )}
        {/* Patička drží u spodní hrany, ať se Uložit u dlouhé poznámky nehledá. */}
        <div className="sticky bottom-0 flex items-center gap-2 rounded-b-2xl border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-sm">
          {konceptAt && (
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-400">
              <Check className="w-3.5 h-3.5" /> Koncept uložen {konceptAt}
            </span>
          )}
          <span className="ml-auto flex items-center gap-2">
            <button onClick={zavrit} className={btnGhost}>Zrušit</button>
            <button onClick={ulozit} disabled={saving || !obsah.trim()} className="btn btn-primary">{saving ? "Ukládám…" : "Uložit"}</button>
          </span>
        </div>
      </div>
    </div>
  );
  return inline ? telo : createPortal(telo, document.body);
}

// Prohlížeč textové poznámky: název, kdo/kdy, vyrenderovaný text, kopírovat/upravit/smazat.
export function TextPrilohaViewer({ priloha, canEdit, onEdit, onDelete, onClose, onToggleHotovo, inline = false }) {
  const [copied, setCopied] = useState(false);
  const [copiedK, setCopiedK] = useState(null);
  const kopirovatPro = async (platform) => {
    try { await zkopirujPoznamku(priloha.obsah || "", platform); setCopiedK(platform); setTimeout(() => setCopiedK(null), 1500); } catch (_) {}
  };
  const kopirovat = async () => {
    try { await navigator.clipboard.writeText(priloha.obsah || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };
  // Overlay jde portalem do document.body (13.8.2026): detail ukolu je sam
  // overlay s transformem a "fixed" uvnitr nej se pocital od nej — nahled
  // poznamky pak visel jen pres cast stranky.
  const telo = (
    <div className={inline ? "" : "fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto p-3 sm:p-6"} onClick={inline ? undefined : (e) => { e.stopPropagation(); onClose(); }}>
      <div className={inline ? "bg-white rounded-2xl border border-violet-200 shadow-sm w-full" : "bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4"} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-slate-800 font-bold">
              <FileText className={`w-4 h-4 shrink-0 ${priloha.hotovo ? "text-emerald-500" : "text-violet-500"}`} />
              <span className="truncate">{priloha.nazev}</span>
              {priloha.hotovo && (
                <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">hotová</span>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Vytvořil {prettyAutor(priloha.autor)} <span aria-hidden className="inline-block w-px h-3 bg-slate-300 align-middle mx-1" /> {fmtDatum(priloha.created_at)}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onToggleHotovo && (
              <button onClick={onToggleHotovo}
                aria-pressed={!!priloha.hotovo}
                className={`p-2 rounded-xl ${priloha.hotovo ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100 hover:text-emerald-600"}`}
                title={priloha.hotovo
                  ? "Hotová poznámka, tohle je finální verze. Klepnutím označení zrušíš."
                  : "Označit jako hotovou: text je finální a už se na něm nepracuje."}>
                {priloha.hotovo ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              </button>
            )}
            <button onClick={kopirovat} title="Kopírovat text" className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 min-h-[44px] sm:min-h-0">{copied ? <span className="text-[11px] font-semibold text-emerald-600 px-1">Zkopírováno</span> : <Copy className="w-4 h-4" />}</button>
            {canEdit && <button onClick={onEdit} title="Upravit" className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 min-h-[44px] sm:min-h-0"><Pencil className="w-4 h-4" /></button>}
            {canEdit && <button onClick={onDelete} title="Smazat" className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 min-h-[44px] sm:min-h-0"><Trash2 className="w-4 h-4" /></button>}
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-4 prose prose-sm max-w-none prose-slate prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-slate-800 prose-a:text-violet-700 whitespace-normal">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE]]}>{priloha.obsah || ""}</ReactMarkdown>
        </div>
        <div className="px-4 pb-4 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3">
          <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Zkopírovat se stejným formátováním pro:</span>
          {[["whatsapp", "WhatsApp"], ["slack", "Slack"], ["telegram", "Telegram"], ["plain", "E-mail / text"]].map(([k, label]) => (
            <button key={k} onClick={() => kopirovatPro(k)}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-700">
              {copiedK === k ? "Zkopírováno" : label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  return inline ? telo : createPortal(telo, document.body);
}

export function TaskDetailModal({ taskId, people, fotoByName, tagSuggestions, kategorie, canManage, canDelete, allTasks, onOpenTask, onClose, onChanged, onDelete, fullPage = false }) {
  // Pod otevřeným detailem nesmí rolovat stránka vzadu, na iOS to vypadá jako zamrznutí.
  useZamekRolovani(!fullPage);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [newSub, setNewSub] = useState("");
  const [newChild, setNewChild] = useState("");
  const [newComment, setNewComment] = useState("");
  const [archivOtevren, setArchivOtevren] = useState(false);   // rozbalený archiv komentářů
  const [mazanyKomentar, setMazanyKomentar] = useState(null);  // potvrzení smazání komentáře
  const [saving, setSaving] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false); // plný modal pro úkol do projektu
  const [showVratit, setShowVratit] = useState(false);     // kontrola: vrácení s komentářem
  const [vratitText, setVratitText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkNazev, setLinkNazev] = useState("");
  const [editPopis, setEditPopis] = useState(false);   // popis: náhled (markdown) vs. úprava
  const [textEditor, setTextEditor] = useState(null); // null | {prilohaId?, nazev, obsah} — editor textové poznámky
  const [textView, setTextView] = useState(null);     // otevřená textová poznámka (prohlížeč)
  const [savingText, setSavingText] = useState(false);
  const [chybaText, setChybaText] = useState("");   // selhané uložení poznámky (koncept zůstává)
  // Sekce sdružují přílohy uvnitř úkolu. Zavřené jsou schválně: u patnácti
  // příloh je jinak detail nekonečný a to podstatné utopené.
  const [otevreneSekce, setOtevreneSekce] = useState(() => new Set());
  const [sekceProPrilohu, setSekceProPrilohu] = useState(null); // id přílohy, u které se vybírá sekce
  const [novaSekce, setNovaSekce] = useState("");
  const fileInputRef = useRef(null);
  const user = getCurrentUser();
  // Historie změn je owner-only (backend vrací 403 komukoli jinému).
  // Deklarace TADY, ať se na ni dole v JSX nesahá dřív, než vznikne (TDZ).
  const jeOwnerHistorie = String(user?.role || "") === "owner";

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/operations-ukoly?taskId=${taskId}`);
      const j = await r.json().catch(() => ({}));
      // FW-146: odkaz ze Slacku může mířit na smazaný úkol nebo úkol, na který
      // uživatel nevidí. Místo věčného „Načítám…" srozumitelná hláška.
      if (r.status === 404) throw new Error("Tento úkol už neexistuje, mohl být smazán.");
      if (r.status === 403) throw new Error("K tomuto úkolu nemáš přístup.");
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setDetail(j);
    } catch (e) { setErr(e.message); }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const post = useCallback(async (payload) => {
    setSaving(true);
    setErr("");
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      await load();
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }, [load, onChanged]);

  const task = detail?.task;
  const saveField = (fields) => post({ id: taskId, fields });

  // Komentáře (27.8.2026): dlouhé vlákno se dá uklidit archivací. Archivovaný
  // komentář se schová pod rozbalovací „Archiv" a jde kdykoli vrátit zpět.
  // Mazání je nevratné, proto se na něj ptáme potvrzením.
  const vsechnyKomentare = detail?.komentare || [];
  const ziveKomentare = vsechnyKomentare.filter((c) => !c.archived);
  const archivKomentare = vsechnyKomentare.filter((c) => c.archived);

  // Odkaz na úkol (6.8.2026): vždy kanonická adresa boardu, ať se dá poslat
  // kolegovi i z meetingu. Fallback pro prohlížeče bez Clipboard API (http).
  const kopirujOdkaz = async () => {
    const url = `${window.location.origin}/operations/ukoly?ukol=${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast("Odkaz na úkol zkopírován.", "success");
    } catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        toast("Odkaz na úkol zkopírován.", "success");
      } catch (_e) { toast("Zkopírování odkazu selhalo.", "error"); }
    }
  };

  // === Přílohy: upload (s optimalizací obrázků), odkaz, smazání, stažení ===
  const uploadFiles = async (files) => {
    setUploading(true); setErr("");
    try {
      for (const f of Array.from(files).slice(0, 5)) {
        // Videa zakázaná — do úkolu patří odkaz (Drive/YouTube), ne soubor.
        if (/^video\//i.test(f.type) || /\.(mp4|mov|avi|mkv|webm|m4v)$/i.test(f.name)) {
          setErr(`"${f.name}" je video - nahraj ho na Drive/YouTube a vlož sem odkaz.`); continue;
        }
        const opt = await optimalizujObrazek(f);
        if (opt.size > 4 * 1024 * 1024) {
          setErr(/\.pdf$/i.test(f.name)
            ? <span>„{f.name}" je větší než 4 MB, <a href="https://www.ilovepdf.com/compress_pdf" target="_blank" rel="noreferrer" className="font-bold underline">zkomprimuj ho zdarma na iLovePDF</a> a nahraj znovu, nebo ho vlož odkazem.</span>
            : `"${f.name}" je i po optimalizaci větší než 4 MB - vlož ho raději odkazem (Drive).`);
          continue;
        }
        const dataUrl = await fileToDataUrl(opt);
        const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify({ action: "upload", ukolId: taskId, filename: opt.name, dataUrl }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Upload selhal");
      }
      await load();
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };
  const addLink = async () => {
    if (!linkUrl.trim()) return;
    setUploading(true); setErr("");
    try {
      const url = /^https?:\/\//i.test(linkUrl.trim()) ? linkUrl.trim() : `https://${linkUrl.trim()}`;
      const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify({ action: "link", ukolId: taskId, url, nazev: linkNazev.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Přidání odkazu selhalo");
      setLinkUrl(""); setLinkNazev(""); setShowLink(false);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };
  // Klíč konceptu: per úkol a per poznámka, nová poznámka má vlastní.
  const klicKonceptu = `ukol-${taskId}-${textEditor?.prilohaId || "novy"}`;
  const saveText = async (nazev, obsah) => {
    setSavingText(true); setErr(""); setChybaText("");
    try {
      const payload = textEditor?.prilohaId
        ? { action: "text-edit", prilohaId: textEditor.prilohaId, nazev, obsah }
        : { action: "text", ukolId: taskId, nazev, obsah };
      const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      // Uloženo na serveru, koncept už není k čemu.
      smazKoncept(klicKonceptu);
      setTextEditor(null);
      await load();
    } catch (e) { setChybaText(e.message || "Uložení selhalo"); }
    finally { setSavingText(false); }
  };
  // Zařazení přílohy do sekce. Ukládá se hned, obrazovka se překreslí bez čekání.
  const ulozSekci = async (prilohaId, sekce) => {
    setSekceProPrilohu(null);
    setNovaSekce("");
    setDetail((d) => (d ? { ...d, prilohy: (d.prilohy || []).map((x) => (String(x.id) === String(prilohaId) ? { ...x, sekce } : x)) } : d));
    if (sekce) setOtevreneSekce((s) => new Set([...s, sekce]));
    try {
      const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify({ action: "sekce", prilohaId, sekce }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); load(); }
  };

  // Hotová poznámka: text je finální a už se na něm nepracuje.
  const prepniHotovo = async (pr) => {
    const hotovo = !pr.hotovo;
    setDetail((d) => (d ? { ...d, prilohy: (d.prilohy || []).map((x) => (String(x.id) === String(pr.id) ? { ...x, hotovo } : x)) } : d));
    // Otevřená poznámka drží vlastní kopii, ať se přepne i v ní.
    setTextView((t) => (t && String(t.id) === String(pr.id) ? { ...t, hotovo } : t));
    try {
      const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify({ action: "hotovo", prilohaId: pr.id, hotovo }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); load(); }
  };

  const deletePriloha = async (prilohaId) => {
    setErr("");
    try {
      const r = await apiFetch("/api/operations-ukoly-file", { method: "POST", body: JSON.stringify({ action: "delete", prilohaId }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      await load();
    } catch (e) { setErr(e.message); }
  };
  const downloadPriloha = async (p) => {
    try {
      const r = await apiFetch(`/api/operations-ukoly-file?key=${encodeURIComponent(p.blob_key)}`);
      if (!r.ok) throw new Error("Stažení selhalo");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = p.nazev || "soubor"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e.message); }
  };
  const col = task ? ([...COLUMNS, ...PINNED_GROUPS].find((c) => c.key === task.status) || COLUMNS[3]) : null;
  const prio = task ? (PRIORITY_META[task.priority] || PRIORITY_META.Medium) : null;
  const subs = detail?.podukoly || [];
  const subsDone = subs.filter((s) => s.hotovo).length;
  const subPct = subs.length ? Math.round((subsDone / subs.length) * 100) : 0;
  // Drag & drop řazení podúkolů — přetáhni řádek; čára ukazuje, KAM se vloží (nad daný řádek).
  const dragSubId = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  // 24.8.2026: přejmenování podúkolu klikem. editSubId = který se právě edituje.
  const [editSubId, setEditSubId] = useState(null);
  const [editSubText, setEditSubText] = useState("");
  const [dragOverId, setDragOverId] = useState(null);
  const onSubDrop = (targetId) => {
    const from = dragSubId.current;
    dragSubId.current = null;
    setDraggingId(null);
    setDragOverId(null);
    if (from == null || from === targetId) return;
    const ids = subs.map((s) => s.id);
    const fi = ids.indexOf(from);
    if (fi < 0) return;
    ids.splice(fi, 1);               // vyndej tažený podúkol
    const ti = ids.indexOf(targetId);
    if (ti < 0) return;
    ids.splice(ti, 0, from);         // vlož PŘED cílový řádek (jednoznačné = čára nad ním)
    // optimistické přeřazení, pak persist na server (post přenačte detail)
    setDetail((d) => (d ? { ...d, podukoly: ids.map((x) => (d.podukoly || []).find((p) => p.id === x)).filter(Boolean) } : d));
    post({ action: "reorderSubtask", id: taskId, order: ids });
  };
  // Projekt: master úkol s navázanými úkoly. Max 20 úkolů na projekt.
  const deti = detail?.deti || [];
  const detiDone = deti.filter((d) => ["hotovo", "archiv"].includes(d.status)).length;
  const jeProjekt = task ? (task.je_projekt === true || deti.length > 0) : false;
  const projPct = deti.length ? Math.round((detiDone / deti.length) * 100) : 0;
  const fmtCz = (iso) => {
    const p = String(iso || "").slice(0, 10).split("-");
    return p.length === 3 ? `${Number(p[2])}. ${Number(p[1])}. ${p[0]}` : "";
  };
  // Telefon: zbytek vlastností (kategorie, projekt, kontrola, viditelnost) je
  // sbalený za jedním řádkem. Na počítači je panel vidět vždycky.
  const [vlastnostiOtevrene, setVlastnostiOtevrene] = useState(false);
  const sideLabel = "label";
  const sideInput = "field";

  const modalContent = (
    <div
      className={fullPage
        ? "bg-slate-100 min-h-[calc(100vh-var(--admin-top,0px))]"
        // 4.9.2026: na telefonu detail přes celou obrazovku (okno s okraji se špatně
        // ovládalo), na počítači zůstává vycentrované okno.
        : "fixed inset-0 z-50 overflow-y-auto overflow-x-hidden overscroll-contain bg-white sm:flex sm:items-start sm:justify-center sm:bg-brand-dark/50 sm:p-4 sm:backdrop-blur-sm"}
      onClick={fullPage ? undefined : onClose}
    >
      <div className={fullPage ? "bg-white w-full min-h-screen" : "min-h-full w-full min-w-0 overflow-x-hidden bg-white sm:my-6 sm:min-h-0 sm:max-w-3xl sm:overflow-hidden sm:rounded-2xl sm:shadow-2xl"} onClick={(e) => e.stopPropagation()}>
        {!task && err ? (
          <div className="p-10 text-center">
            <p className="text-sm font-semibold text-slate-700">{err}</p>
            <button type="button" onClick={onClose} className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ChevronLeft className="h-4 w-4" /> Zpět na úkoly
            </button>
          </div>
        ) : !task ? (
          <div className="p-10 text-center text-slate-400 text-sm">Načítám…</div>
        ) : (
          <>
            {/* 4.9.2026 (schváleno): hlavička je gradientová jako u stránek, vlevo
                šipka zpět, akce jsou ikony. Na telefonu je detail přes celou
                obrazovku, na počítači zůstává okno. */}
            <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 pb-3 pt-3 sm:px-5">
              <div className="mb-2 flex items-center gap-2">
                <button onClick={onClose} className="btn-ikona btn-ikona-tmava" aria-label="Zpět" title="Zpět">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="min-w-0 flex-1" />
                {/* Hotovo přímo v hlavičce (6.8.2026; 7.8.2026 přesunuto vedle archivace
                    a zmenšeno na pouhou ikonu). Aktivní teprve když je v úkolu všechno
                    splněné — odškrtané podúkoly a u projektu i všechny navázané úkoly.
                    Backend hlídá totéž (409). */}
                {(() => {
                  if (["hotovo", "archiv"].includes(task.status)) return null;
                  if (task.kontrola) return null; // kontrola má vlastní flow (Odevzdat ke kontrole)
                  const subyOk = subs.length === 0 || subsDone === subs.length;
                  const detiOk = deti.length === 0 || detiDone === deti.length;
                  const muze = subyOk && detiOk;
                  const duvod = !subyOk
                    ? `Nejdřív odškrtni všechny podúkoly (${subsDone}/${subs.length}).`
                    : !detiOk
                      ? `Projekt jde uzavřít až po dokončení všech úkolů (zbývá ${deti.length - detiDone}).`
                      : "Označit úkol jako hotový";
                  return (
                    <button onClick={() => post({ id: taskId, status: "hotovo" })} disabled={!muze || saving} title={duvod}
                      aria-label="Označit úkol jako hotový" className="btn-ikona btn-ikona-tmava">
                      <CheckCircle2 className="h-5 w-5" />
                    </button>
                  );
                })()}
                {(() => {
                  // Pravidla 17.7.: archivovat smí zadavatel (nebo owner); mazat owner kdykoli,
                  // tvůrce jen do 2 hodin od vytvoření (překlep hned po zadání).
                  const jeOwnerU = (getCurrentUser()?.role || "") === "owner";
                  const jeZadavatelU = String(user?.name || "").toLowerCase() === String(task.zadavatel || "").toLowerCase();
                  const smiArchiv = (jeOwnerU || jeZadavatelU) && task.status !== "archiv";
                  const do2h = task.created_at && (Date.now() - new Date(task.created_at).getTime()) < 2 * 60 * 60 * 1000;
                  const jeTvurce = String(task.created_by || "").toLowerCase() === String(user?.email || "").toLowerCase();
                  const smiSmazat = jeOwnerU || (jeTvurce && do2h);
                  return (
                    <>
                      {smiArchiv && (
                        <button onClick={() => post({ id: taskId, status: "archiv" })} className="btn-ikona btn-ikona-tmava"
                          aria-label="Archivovat" title="Archivovat - úkol ztratil relevanci, nebude se dělat">
                          <Archive className="h-5 w-5" />
                        </button>
                      )}
                      {smiSmazat && (
                        <button onClick={() => onDelete(task)} className="btn-ikona btn-ikona-tmava" aria-label="Smazat úkol"
                          title={jeOwnerU ? "Smazat úkol" : "Smazat úkol (jako tvůrce můžeš do 2 h od vytvoření)"}>
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </>
                  );
                })()}
                <button onClick={kopirujOdkaz} className="btn-ikona btn-ikona-tmava hidden sm:inline-flex"
                  aria-label="Zkopírovat odkaz" title="Zkopírovat odkaz na úkol">
                  <Link2 className="h-5 w-5" />
                </button>
                {!fullPage && (
                  <button onClick={(e) => {
                      const kam = `${window.location.pathname}?ukol=${taskId}`;
                      // Ctrl (na Macu Cmd) drží zvyk z prohlížeče: opravdové nové okno.
                      // Bez něj se úkol otevře jako záložka uvnitř adminu.
                      if (e.ctrlKey || e.metaKey) { window.open(kam, "_blank"); return; }
                      window.dispatchEvent(new CustomEvent(UDALOST_ZALOZKA, {
                        detail: { to: kam, title: task?.nazev || "Úkol" },
                      }));
                    }}
                    className="btn-ikona btn-ikona-tmava hidden sm:inline-flex"
                    aria-label="Otevřít v záložce" title="Otevřít v záložce adminu (s Ctrl v novém okně prohlížeče)">
                    <Maximize2 className="h-5 w-5" />
                  </button>
                )}
              </div>
              {/* Hlášení #184 (30. 10. 2026): dlouhý název se zalamuje na další řádky, místo useknutí.
                  Enter uloží (název je jeden řádek, nový řádek se nevkládá). */}
              <textarea
                rows={1}
                className="block w-full resize-none overflow-hidden break-words rounded-xl bg-transparent px-1 py-0.5 font-serif text-[22px] font-bold leading-tight text-white focus:bg-white/10 focus:outline-none"
                defaultValue={task.nazev}
                ref={prizpusobVysku}
                onInput={(e) => prizpusobVysku(e.target)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                onBlur={(e) => { const v = e.target.value.replace(/\s*\n+\s*/g, " ").trim(); if (v && v !== task.nazev) saveField({ nazev: v }); }}
              />
              <div className="mt-2 flex flex-wrap items-center gap-1.5 px-1">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">
                  {col.ikona && <col.ikona className="h-3.5 w-3.5" />} {col.label}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${
                  task.priority === "High" ? "bg-rose-500" : task.priority === "Low" ? "bg-white/15" : "bg-amber-500"
                }`}>
                  <prio.icon className="h-3 w-3" /> {prio.label}
                </span>
                {task.kategorie && <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">{task.kategorie}</span>}
                {task.uroven === "basic" && <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">basic</span>}
                {jeProjekt && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white" title="Projekt - sdružuje navázané úkoly">
                    <FolderKanban className="h-3 w-3" /> projekt
                  </span>
                )}
                {task.kontrola && <span className="rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-bold text-white" title="Jako hotový označuje zadavatel">kontrola</span>}
                {task.zdroj === "slack" && <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white" title="Zadáno přes Slack agenta">ze Slacku</span>}
                {task.rutina_id && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white"><Repeat className="h-3 w-3" /> rutina</span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-white/70">
                {task.zadavatel && <span className="demo-sensitive">Zadal(a): {task.zadavatel}</span>}
                {task.vytvoreno && <span>Vytvořeno {fmtCz(task.vytvoreno)}</span>}
                {task.hotovo_kdy && <span className="font-semibold text-emerald-300">Hotovo {fmtCz(task.hotovo_kdy)}</span>}
              </div>
            </div>

            {err && <div className="mx-5 mt-3 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl p-2">{err}</div>}

            {/* 4.9.2026: na telefonu jsou vlastnosti (pilot, termín, sloupec, priorita)
                nahoře — dřív byly až pod komentáři a historií, tedy nejdál od ruky. */}
            <div className="flex min-w-0 flex-col md:grid md:grid-cols-[1fr,250px]">
              {/* Levá část: popis, podúkoly, komentáře */}
              <div className="order-2 min-w-0 space-y-5 p-4 sm:p-5 md:order-1">
                {/* Navázaný úkol: výrazný pruh s proklikem na projekt */}
                {detail.parent && (
                  <button onClick={() => onOpenTask?.(detail.parent.id)}
                    className="flex w-full items-center gap-2 rounded-xl border border-brand/20 bg-brand-50 px-3 py-2.5 text-left">
                    <FolderKanban className="h-4 w-4 shrink-0 text-brand-deep" />
                    <span className="min-w-0 truncate text-sm text-brand-deep">Součást projektu: <span className="font-bold">{detail.parent.nazev}</span></span>
                    <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-brand" />
                  </button>
                )}
                {/* Kontrola: odevzdání / schválení / vrácení. Slack DM řeší backend (Joky). */}
                {(() => {
                  const jeOwnerK = (getCurrentUser()?.role || "") === "owner";
                  const jeZadavatelK = String(user?.name || "").toLowerCase() === String(task.zadavatel || "").toLowerCase();
                  const smiRozhodnout = jeOwnerK || jeZadavatelK;
                  if (task.ke_kontrole_kdy) {
                    return (
                      <div className="bg-amber-50 border border-amber-300 rounded-xl px-3 py-2.5 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-amber-900">
                          <ScanSearch className="w-4 h-4 shrink-0" />
                          <span><span className="font-bold demo-sensitive">{task.ke_kontrole_od || task.pilot}</span> odevzdal(a) úkol ke kontrole {fmtCz(task.ke_kontrole_kdy)}.</span>
                        </div>
                        {smiRozhodnout ? (
                          <>
                            <div className="flex flex-wrap gap-1.5">
                              <button onClick={() => post({ action: "kontrolaSchvalit", id: taskId })} disabled={saving}
                                className="btn btn-primary">
                                <Check className="w-4 h-4" /> Schválit jako Hotovo
                              </button>
                              <button onClick={() => setShowVratit((v) => !v)} disabled={saving}
                                className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-xl hover:bg-amber-100">
                                <Undo2 className="w-4 h-4" /> Vrátit s komentářem
                              </button>
                            </div>
                            {showVratit && (
                              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); post({ action: "kontrolaVratit", id: taskId, text: vratitText.trim() }); setShowVratit(false); setVratitText(""); }}>
                                <input className="field" placeholder="Co je potřeba dodělat…" value={vratitText} onChange={(e) => setVratitText(e.target.value)} autoFocus />
                                <button type="submit" disabled={saving} className="btn btn-primary">Vrátit</button>
                              </form>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-amber-700">Čeká na schválení zadavatelem ({task.zadavatel || "owner"}), dostal zprávu ve Slacku od Jokyho.</div>
                        )}
                      </div>
                    );
                  }
                  if (task.kontrola && !smiRozhodnout && !["hotovo", "archiv"].includes(task.status)) {
                    return (
                      <button onClick={() => post({ action: "keKontrole", id: taskId })} disabled={saving}
                        className="w-full flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 hover:bg-amber-100 text-left">
                        <ScanSearch className="w-4 h-4 shrink-0 text-amber-700" />
                        <span className="text-sm font-bold text-amber-900">Odevzdat ke kontrole</span>
                        <span className="text-xs text-amber-700 ml-auto">zadavatel ({task.zadavatel || "owner"}) dostane zprávu od Jokyho</span>
                      </button>
                    );
                  }
                  return null;
                })()}
                {/* Projekt: velký progress nahoře - na první pohled kolik je splněno */}
                {jeProjekt && deti.length > 0 && (
                  <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl px-4 py-3">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-sm font-bold text-indigo-900">Plnění projektu</span>
                      <span className="text-2xl font-black text-indigo-700 tabular-nums ml-auto">{projPct} %</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-indigo-100 overflow-hidden">
                      <div className="h-full bg-indigo-500 transition-all" style={{ width: `${projPct}%` }} />
                    </div>
                    <div className="text-[11px] text-indigo-700/80 mt-1">{detiDone} z {deti.length} úkolů dokončeno, projekt jde uzavřít až po všech</div>
                  </div>
                )}
                <div>
                  <div className={`${sideLabel} mb-1.5 flex items-center gap-2`}>
                    <span>Popis</span>
                    {!editPopis && task.popis && (
                      <button type="button" onClick={() => setEditPopis(true)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:text-violet-800 normal-case tracking-normal">
                        <Pencil className="w-3 h-3" /> Upravit
                      </button>
                    )}
                  </div>
                  {/* 20.7.2026: popis je vidět CELÝ (markdown náhled bez omezení výšky),
                      úprava v textarea, která roste s obsahem. Podporuje formátování. */}
                  {(editPopis || !task.popis) ? (
                    <PopisEditor hodnota={task.popis} autoFocus={editPopis}
                      onSave={(v) => saveField({ popis: v })} onClose={() => setEditPopis(false)} />
                  ) : (
                    <div onClick={() => setEditPopis(true)} title="Klikni pro úpravu"
                      className="cursor-text bg-slate-50 rounded-xl px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-100/70 transition-colors
                        prose prose-sm max-w-none prose-slate prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                        prose-strong:text-slate-800 prose-a:text-violet-700">
                      {/* Feedback #64 (13.8.2026): poznamky psane po radcich se slevaly
                          do jednoho odstavce — single \n je nove tvrdy zlom. */}
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(task.popis || "").replace(/\n(?!\n)/g, "  \n")}</ReactMarkdown>
                    </div>
                  )}
                </div>

                {/* Podúkoly s progress barem. U projektu se checklist potlačí -
                    projekt se řídí navázanými úkoly (checklist zůstane viditelný,
                    jen pokud tam už nějaké body jsou). */}
                {!(jeProjekt && subs.length === 0) && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-bold text-slate-800">Podúkoly</span>
                    {jeProjekt && <span className="text-[11px] text-slate-400">(projekt se řídí navázanými úkoly níže)</span>}
                    {subs.length > 0 && (
                      <>
                        <span className="text-xs text-slate-400 tabular-nums">{subsDone}/{subs.length}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-[140px]">
                          <div className="h-full bg-emerald-400 transition-all" style={{ width: `${subPct}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {subs.map((s) => {
                      const jeCil = canManage && dragOverId === s.id && draggingId !== s.id;
                      return (
                      // FW-147: přetahuje se jen za úchyt, text podúkolu jde normálně označit a zkopírovat.
                      <div key={s.id} data-podukol={s.id}
                        onDragOver={canManage ? (e) => { e.preventDefault(); setDragOverId(s.id); } : undefined}
                        onDragLeave={canManage ? () => setDragOverId((cur) => (cur === s.id ? null : cur)) : undefined}
                        onDrop={canManage ? () => onSubDrop(s.id) : undefined}
                        onDragEnd={canManage ? () => { dragSubId.current = null; setDraggingId(null); setDragOverId(null); } : undefined}
                        className={`flex items-center gap-2 group rounded-xl px-2 py-1.5 hover:bg-slate-50 border-t-2 transition-colors ${jeCil ? "border-violet-500 bg-violet-50/50" : "border-transparent"} ${draggingId === s.id ? "opacity-40" : ""}`}>
                        {canManage && (
                          <span
                            draggable
                            onDragStart={(e) => {
                              dragSubId.current = s.id; setDraggingId(s.id);
                              try {
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData("text/plain", "");
                                const radek = e.currentTarget.closest("[data-podukol]");
                                if (radek) e.dataTransfer.setDragImage(radek, 12, 12);
                              } catch (_) {}
                            }}
                            className="-my-1 -ml-1 flex shrink-0 cursor-grab touch-none items-center self-stretch px-1 active:cursor-grabbing"
                            title="Přetáhni pro změnu pořadí"
                            aria-label="Přetáhni pro změnu pořadí"
                          >
                            <GripVertical className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-400" />
                          </span>
                        )}
                        <input type="checkbox" checked={s.hotovo === true} onChange={() => post({ action: "toggleSubtask", subtaskId: s.id })}
                          className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-300 cursor-pointer" />
                        {editSubId === s.id ? (
                          <input
                            autoFocus
                            value={editSubText}
                            onChange={(e) => setEditSubText(e.target.value)}
                            onBlur={() => {
                              const t = editSubText.trim();
                              setEditSubId(null);
                              if (t && t !== s.text) post({ action: "renameSubtask", subtaskId: s.id, text: t });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                              if (e.key === "Escape") { e.preventDefault(); setEditSubId(null); }
                            }}
                            className="text-sm flex-1 px-2 py-0.5 rounded border-2 border-violet-400 bg-violet-50 outline-none"
                          />
                        ) : (
                          <span
                            onClick={canManage ? () => {
                              // Po označení části textu (kopírování) se nepřepíná do úprav.
                              const vyber = typeof window !== "undefined" ? String(window.getSelection?.() || "") : "";
                              if (vyber.trim()) return;
                              setEditSubId(s.id); setEditSubText(s.text);
                            } : undefined}
                            title={canManage ? "Klikni pro přepsání textu" : undefined}
                            className={`text-sm flex-1 select-text ${canManage ? "cursor-text hover:bg-violet-50 rounded px-1 -mx-1" : ""} ${s.hotovo ? "line-through text-slate-400" : "text-slate-700"}`}
                          >{s.text}</span>
                        )}
                        {canManage && (
                          <button onClick={() => post({ action: "deleteSubtask", subtaskId: s.id })}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600 shrink-0" title="Smazat podúkol"><X className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <form className="flex gap-2 mt-1.5" onSubmit={(e) => { e.preventDefault(); if (newSub.trim()) { post({ action: "addSubtask", id: taskId, text: newSub.trim() }); setNewSub(""); } }}>
                    <input className="field" placeholder="Přidat podúkol…" value={newSub} onChange={(e) => setNewSub(e.target.value)} />
                    <button type="submit" disabled={saving || !newSub.trim()} className="btn btn-primary"><Plus className="w-4 h-4" /></button>
                  </form>
                </div>
                )}

                {/* Projekt: navázané úkoly (plnění projektu závisí na nich) */}
                {(deti.length > 0 || (canManage && !task.parent_id && jeProjekt)) && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FolderKanban className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-bold text-slate-800">Úkoly projektu</span>
                      {deti.length > 0 && (
                        <span className="text-xs text-slate-400 tabular-nums">{detiDone}/{deti.length}, max 20</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {deti.map((d) => (
                        <button key={d.id} onClick={() => onOpenTask?.(d.id)}
                          className="w-full flex items-center gap-2 text-left rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/40 px-2.5 py-1.5">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${["hotovo", "archiv"].includes(d.status) ? "bg-emerald-500" : d.status === "v_procesu" ? "bg-violet-500" : "bg-slate-300"}`} />
                          <span className={`text-sm flex-1 truncate ${["hotovo", "archiv"].includes(d.status) ? "line-through text-slate-400" : "text-slate-700"}`}>{d.nazev}</span>
                          {d.deadline && <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{fmtCz(d.deadline)}</span>}
                          <span className="text-[11px] text-slate-400 shrink-0 demo-sensitive">{d.pilot}</span>
                          <span className="text-[11px] text-slate-400 shrink-0">{STATUS_LABELS[d.status] || d.status}</span>
                        </button>
                      ))}
                    </div>
                    {canManage && !task.parent_id && (
                      deti.length >= 20 ? (
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-2.5 py-1.5 mt-1.5">Projekt má strop 20 úkolů - rozděl ho na menší projekty.</div>
                      ) : (
                      <>
                      <form className="flex gap-2 mt-1.5" onSubmit={(e) => { e.preventDefault(); if (newChild.trim()) { post({ action: "create", task: { nazev: newChild.trim(), parent_id: taskId, pilot: task.pilot, kategorie: task.kategorie, status: ["hotovo", "archiv"].includes(task.status) ? "tydenni" : task.status } }); setNewChild(""); } }}>
                        <input className="field" placeholder="Rychle přidat úkol (jen název)…" value={newChild} onChange={(e) => setNewChild(e.target.value)} />
                        <button type="submit" disabled={saving || !newChild.trim()} className="btn btn-primary"><Plus className="w-4 h-4" /></button>
                      </form>
                      <button onClick={() => setShowAddChild(true)}
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl px-2.5 py-1.5 hover:bg-indigo-100">
                        <Plus className="w-3.5 h-3.5" /> Přidat úkol s detaily (pilot, deadline, podúkoly…)
                      </button>
                      </>
                      )
                    )}
                  </div>
                )}

                {/* Přílohy: soubory/fotky (optimalizované) + odkazy */}
                {(() => {
                  const prilohy = detail.prilohy || [];
                  const sekceVUkolu = [...new Set(prilohy.map((x) => String(x.sekce || "").trim()).filter(Boolean))];

                  /** Jeden řádek přílohy. Používá ho plochý seznam i sekce. */
                  const radekPrilohy = (pr) => {
                    const smiSmazatP = canManage || String(pr.autor || "").toLowerCase() === String(user?.email || "").toLowerCase();
                    const jeObrazek = pr.typ === "soubor" && /^image\//.test(pr.mime);
                    const vybirasSekci = String(sekceProPrilohu) === String(pr.id);
                    return (
                      <div key={pr.id} data-priloha={pr.id} className="rounded-xl border border-slate-100 hover:border-slate-200 bg-white">
                        <div className="flex items-center gap-2.5 group px-2 py-1.5">
                          {pr.typ === "odkaz" ? (
                            <span className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0"><Link2 className="w-4 h-4" /></span>
                          ) : pr.typ === "text" ? (
                            <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${pr.hotovo ? "bg-emerald-50 text-emerald-600" : "bg-violet-50 text-violet-600"}`}><FileText className="w-4 h-4" /></span>
                          ) : jeObrazek ? (
                            <PrilohaThumb blobKey={pr.blob_key} />
                          ) : (
                            <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Paperclip className="w-4 h-4" /></span>
                          )}
                          {pr.typ === "odkaz" ? (
                            <a href={pr.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-sky-700 hover:underline flex-1 truncate min-w-0">{pr.nazev || pr.url}</a>
                          ) : pr.typ === "text" ? (
                            <button onClick={() => setTextView(pr)} className="flex-1 min-w-0 text-left" title="Otevřít poznámku">
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="text-sm font-medium text-slate-700 hover:text-violet-700 truncate">{pr.nazev}</span>
                                {pr.hotovo && (
                                  <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">hotová</span>
                                )}
                              </span>
                              <span className="block text-[11px] text-slate-400 truncate">Vytvořil {prettyAutor(pr.autor)} <span aria-hidden className="inline-block w-px h-3 bg-slate-300 align-middle mx-1" /> {fmtDatum(pr.created_at)}</span>
                            </button>
                          ) : (
                            <button onClick={() => downloadPriloha(pr)} className="text-sm font-medium text-slate-700 hover:text-violet-700 text-left flex-1 truncate min-w-0" title="Stáhnout">{pr.nazev}</button>
                          )}
                          {pr.typ === "soubor" && <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{fmtVelikost(pr.velikost)}</span>}
                          {/* Finální verze poznámky. U souborů a odkazů to nedává smysl. */}
                          {pr.typ === "text" && (
                            <button onClick={() => prepniHotovo(pr)}
                              className={`p-1 shrink-0 ${pr.hotovo ? "text-emerald-600 hover:text-rose-600" : "text-slate-400 hover:text-emerald-600"}`}
                              aria-pressed={!!pr.hotovo}
                              title={pr.hotovo
                                ? "Hotová poznámka, tohle je finální verze. Klepnutím označení zrušíš."
                                : "Označit jako hotovou: text je finální a už se na něm nepracuje."}>
                              {pr.hotovo ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                            </button>
                          )}
                          <button onClick={() => { setSekceProPrilohu(vybirasSekci ? null : pr.id); setNovaSekce(""); }}
                            className="p-1 text-slate-400 hover:text-violet-600 shrink-0"
                            title="Zařadit do sekce: přílohy se dají seskupit (E-maily, SMS…) a sekce se pak sbalí">
                            <FolderTree className="w-3.5 h-3.5" />
                          </button>
                          {pr.typ === "soubor" && (
                            <button onClick={() => downloadPriloha(pr)} className="p-1 text-slate-400 hover:text-violet-600 shrink-0" title="Stáhnout"><Download className="w-3.5 h-3.5" /></button>
                          )}
                          {smiSmazatP && (
                            <button onClick={() => deletePriloha(pr.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600 shrink-0" title="Smazat přílohu"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                        {/* Výběr sekce rovnou u řádku, žádný další dialog. */}
                        {vybirasSekci && (
                          <div className="border-t border-slate-100 bg-slate-50 px-2.5 py-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {sekceVUkolu.map((n) => (
                                <button key={n} onClick={() => ulozSekci(pr.id, n)}
                                  className={`text-xs font-semibold rounded-full border px-2.5 py-1 ${String(pr.sekce || "") === n ? "border-violet-400 bg-violet-100 text-violet-800" : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}>
                                  {n}
                                </button>
                              ))}
                              {String(pr.sekce || "").trim() && (
                                <button onClick={() => ulozSekci(pr.id, "")}
                                  className="btn btn-secondary">
                                  Bez sekce
                                </button>
                              )}
                            </div>
                            <form className="flex gap-1.5 mt-2" onSubmit={(e) => { e.preventDefault(); if (novaSekce.trim()) ulozSekci(pr.id, novaSekce.trim()); }}>
                              <input className="field" placeholder="Nová sekce, např. E-maily kampaň" value={novaSekce}
                                onChange={(e) => setNovaSekce(e.target.value)} maxLength={60} />
                              <button type="submit" disabled={!novaSekce.trim()} className="btn btn-primary">Zařadit</button>
                            </form>
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div>
                      {/* 5.9.2026: popisek je nad tlačítky, ne vedle nich. Vedle se na
                          telefonu lámal na dva řádky a text lezl k hraně. */}
                      <div className="mb-2 flex items-center gap-2">
                        <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Přílohy a odkazy</span>
                        {prilohy.length > 0 && <span className="text-[11px] font-bold tabular-nums text-slate-500">{prilohy.length}/15</span>}
                      </div>
                      <div className="mb-2 grid grid-cols-3 gap-2">
                        <button onClick={() => fileInputRef.current?.click()} disabled={uploading || prilohy.length >= 15}
                          className="btn btn-secondary btn-sm min-w-0">
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Soubor
                        </button>
                        <button onClick={() => setShowLink((v) => !v)} disabled={prilohy.length >= 15} className="btn btn-secondary btn-sm min-w-0">
                          <Link2 className="h-4 w-4" /> Odkaz
                        </button>
                        <button onClick={() => setTextEditor({})} disabled={prilohy.length >= 15} className="btn btn-secondary btn-sm min-w-0">
                          <FileText className="h-4 w-4" /> Poznámka
                        </button>
                      </div>
                      <input ref={fileInputRef} type="file" multiple className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.pages,.numbers,.key"
                        onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)} />
                      {showLink && (
                        <form className="flex flex-col sm:flex-row gap-2 mb-2 p-2.5 bg-sky-50/60 border border-sky-100 rounded-xl"
                          onSubmit={(e) => { e.preventDefault(); addLink(); }}>
                          <input className="field" placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
                          <input className="field sm:max-w-[180px]" placeholder="Popisek (volitelně)" value={linkNazev} onChange={(e) => setLinkNazev(e.target.value)} />
                          <button type="submit" disabled={uploading || !linkUrl.trim()} className="btn btn-primary"><Plus className="w-4 h-4" /></button>
                        </form>
                      )}
                      {prilohy.length === 0 && !showLink && (
                        <div className="text-xs text-slate-400">Zatím nic. Fotky se při nahrání automaticky zmenší kvůli úložišti.</div>
                      )}
                      {(() => {
                        // Sekce v pořadí, jak vznikly. Prázdné „Ostatní" na konec.
                        const nazvy = [];
                        for (const pr of prilohy) {
                          const n = String(pr.sekce || "").trim();
                          if (n && !nazvy.includes(n)) nazvy.push(n);
                        }
                        const bezSekce = prilohy.filter((pr) => !String(pr.sekce || "").trim());
                        const skupiny = nazvy.length
                          ? [
                              ...nazvy.map((n) => ({ nazev: n, polozky: prilohy.filter((pr) => String(pr.sekce || "").trim() === n) })),
                              ...(bezSekce.length ? [{ nazev: "Ostatní", polozky: bezSekce }] : []),
                            ]
                          : [];
                        // Dokud si nikdo sekci nepojmenuje, zůstává seznam plochý.
                        if (!skupiny.length) return <div className="space-y-1">{prilohy.map(radekPrilohy)}</div>;
                        return (
                          <div className="space-y-1">
                            {skupiny.map((sk) => {
                              const otevrena = otevreneSekce.has(sk.nazev);
                              return (
                                <div key={sk.nazev} className="rounded-xl border border-slate-200 overflow-hidden" data-sekce={sk.nazev}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const box = e.currentTarget.parentElement;
                                      setOtevreneSekce((st) => {
                                        const d = new Set(st);
                                        if (d.has(sk.nazev)) d.delete(sk.nazev);
                                        else {
                                          d.add(sk.nazev);
                                          // Po rozbalení doskrolovat, ať je vidět, co se otevřelo.
                                          setTimeout(() => box?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
                                        }
                                        return d;
                                      });
                                    }}
                                    aria-expanded={otevrena}
                                    className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50"
                                  >
                                    <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${otevrena ? "rotate-180" : ""}`} />
                                    <span className="flex-1 min-w-0 truncate text-sm font-bold text-slate-800">{sk.nazev}</span>
                                    <span className="text-xs text-slate-400 tabular-nums shrink-0">{sk.polozky.length}</span>
                                  </button>
                                  {otevrena && <div className="space-y-1 border-t border-slate-200 bg-slate-50/60 p-1.5">{sk.polozky.map(radekPrilohy)}</div>}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {fullPage && textEditor && (
                        <div className="mt-3">
                          <TextPrilohaEditor key={klicKonceptu} inline initialNazev={textEditor.nazev || ""} initialObsah={textEditor.obsah || ""} saving={savingText}
                            draftKey={klicKonceptu} chyba={chybaText}
                            onSave={saveText} onClose={() => { setChybaText(""); setTextEditor(null); }} />
                        </div>
                      )}
                      {fullPage && textView && (
                        <div className="mt-3">
                          <TextPrilohaViewer inline priloha={textView}
                            canEdit={canManage || String(textView.autor || "").toLowerCase() === String(user?.email || "").toLowerCase()}
                            onEdit={() => { setTextEditor({ prilohaId: textView.id, nazev: textView.nazev, obsah: textView.obsah }); setTextView(null); }}
                            onDelete={() => { const id = textView.id; setTextView(null); deletePriloha(id); }}
                            onToggleHotovo={() => prepniHotovo(textView)}
                            onClose={() => setTextView(null)} />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Komentáře jako bublinky s avatary */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-bold text-slate-800">Komentáře</span>
                    {(detail.komentare || []).length > 0 && <span className="text-xs text-slate-400 tabular-nums">{detail.komentare.length}</span>}
                  </div>
                  <div className="space-y-2.5">
                    {ziveKomentare.map((c) => (
                      <div key={c.id} className="flex items-start gap-2.5 group">
                        <Avatar name={c.autor_jmeno || c.autor_email} foto={fotoByName?.[c.autor_jmeno] || ""} className="w-7 h-7 mt-0.5" textClassName="text-[11px]" />
                        <div className="flex-1 min-w-0 bg-slate-50 rounded-2xl rounded-tl-sm px-3 py-2">
                          <div className="flex items-baseline gap-2 text-xs">
                            <span className="font-bold text-slate-700 demo-sensitive">{c.autor_jmeno || c.autor_email}</span>
                            <span className="text-slate-400">{c.vytvoreno ? new Date(c.vytvoreno).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            {c.archived && <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">archiv</span>}
                            <span className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                              <button onClick={() => post({ action: "archiveComment", commentId: c.id, archived: !c.archived })}
                                title={c.archived ? "Vrátit komentář zpět do vlákna" : "Archivovat komentář (schová se pod Archiv)"}
                                className="p-0.5 text-slate-400 hover:text-slate-700">
                                {c.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                              </button>
                              {canManage && (
                                <button onClick={() => setMazanyKomentar(c)}
                                  title="Smazat komentář"
                                  className="p-0.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </span>
                          </div>
                          <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words"><ZminkaText text={c.text} /></div>
                        </div>
                      </div>
                    ))}
                    {vsechnyKomentare.length === 0 && <div className="text-xs text-slate-400">Zatím žádný komentář.</div>}
                    {ziveKomentare.length === 0 && archivKomentare.length > 0 && (
                      <div className="text-xs text-slate-400">Všechny komentáře jsou v archivu.</div>
                    )}

                    {archivKomentare.length > 0 && (
                      <div className="pt-1">
                        <button type="button" onClick={() => setArchivOtevren((v) => !v)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">
                          <Archive className="w-3.5 h-3.5" />
                          Archiv komentářů
                          <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                          <span className="tabular-nums">{archivKomentare.length}</span>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${archivOtevren ? "rotate-180" : ""}`} />
                        </button>
                        {archivOtevren && (
                          <div className="mt-2 space-y-2.5 opacity-75">
                            {archivKomentare.map((c) => (
                      <div key={c.id} className="flex items-start gap-2.5 group">
                        <Avatar name={c.autor_jmeno || c.autor_email} foto={fotoByName?.[c.autor_jmeno] || ""} className="w-7 h-7 mt-0.5" textClassName="text-[11px]" />
                        <div className="flex-1 min-w-0 bg-slate-50 rounded-2xl rounded-tl-sm px-3 py-2">
                          <div className="flex items-baseline gap-2 text-xs">
                            <span className="font-bold text-slate-700 demo-sensitive">{c.autor_jmeno || c.autor_email}</span>
                            <span className="text-slate-400">{c.vytvoreno ? new Date(c.vytvoreno).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            {c.archived && <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">archiv</span>}
                            <span className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                              <button onClick={() => post({ action: "archiveComment", commentId: c.id, archived: !c.archived })}
                                title={c.archived ? "Vrátit komentář zpět do vlákna" : "Archivovat komentář (schová se pod Archiv)"}
                                className="p-0.5 text-slate-400 hover:text-slate-700">
                                {c.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                              </button>
                              {canManage && (
                                <button onClick={() => setMazanyKomentar(c)}
                                  title="Smazat komentář"
                                  className="p-0.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </span>
                          </div>
                          <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words"><ZminkaText text={c.text} /></div>
                        </div>
                      </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <form className="flex items-center gap-2 mt-2.5" onSubmit={(e) => { e.preventDefault(); if (newComment.trim()) { post({ action: "addComment", id: taskId, text: newComment.trim() }); setNewComment(""); } }}>
                    <Avatar name={user?.name || ""} foto={fotoByName?.[user?.name] || ""} className="w-7 h-7" textClassName="text-[11px]" />
                    <ZminkaInput className="field" placeholder="Napsat komentář… (@ označí člověka)"
                      value={newComment} onChange={setNewComment} lide={detail.zminitelni || []} />
                    <button type="submit" disabled={saving || !newComment.trim()} className="btn btn-primary"><Send className="w-4 h-4" /></button>
                  </form>
                  <ConfirmDialog
                    open={!!mazanyKomentar}
                    title="Smazat komentář?"
                    description={mazanyKomentar
                      ? <>Komentář od <strong>{mazanyKomentar.autor_jmeno || mazanyKomentar.autor_email}</strong> se nenávratně smaže. Když ho chceš jen schovat, použij archivaci.</>
                      : ""}
                    confirmLabel="Smazat"
                    variant="danger"
                    onCancel={() => setMazanyKomentar(null)}
                    onConfirm={() => { const c = mazanyKomentar; setMazanyKomentar(null); if (c) post({ action: "deleteComment", commentId: c.id }); }}
                  />
                </div>

                {/* Historie změn (7.8.2026) — kdo a kdy posunul deadline, přehodil pilota…
                    Jen owner: je to dohledávání "kdo mi to změnil", backend hlídá totéž. */}
                <UkolHistorie enabled={jeOwnerHistorie} endpoint={`/api/operations-ukoly?historie=1&id=${taskId}`} />
              </div>

              {/* --- Telefon: čtyři nejčastěji měněné vlastnosti jako dlaždice 2×2
                  (schváleno 5. 9. 2026). Nativní výběr je průhledně přes dlaždici,
                  takže se otevře systémový picker, ale vypadá to jako appka. --- */}
              <div className="order-1 border-b-8 border-slate-100 md:hidden">
                <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
                  <div className="relative flex min-h-[64px] items-center gap-2.5 px-4 py-2">
                    <Avatar name={task.pilot} foto={fotoByName?.[task.pilot] || ""} className="h-9 w-9 shrink-0" textClassName="text-[11px]" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Pilot</span>
                      <span className="block truncate text-[15px] font-bold text-slate-900 demo-sensitive">{task.pilot || "Nepřiřazeno"}</span>
                    </span>
                    <select aria-label="Pilot" className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      value={task.pilot || ""} onChange={(e) => saveField({ pilot: e.target.value })}>
                      <option value="">Nepřiřazeno</option>
                      {people.map((p) => <option key={p} value={p}>{p}</option>)}
                      {task.pilot && !people.includes(task.pilot) && <option value={task.pilot}>{task.pilot}</option>}
                    </select>
                  </div>
                  {(() => {
                    const jeOwnerD = (getCurrentUser()?.role || "") === "owner";
                    const jeZadavatelD = String(user?.name || "").toLowerCase() === String(task.zadavatel || "").toLowerCase();
                    const smiDeadline = jeOwnerD || jeZadavatelD || !task.zadavatel;
                    const dl = deadlineLabel(task.deadline, todayIso());
                    return (
                      <div className="relative flex min-h-[64px] items-center gap-2.5 px-4 py-2">
                        <Calendar className={`h-5 w-5 shrink-0 ${dl?.tone === "overdue" ? "text-rose-500" : "text-slate-400"}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Termín</span>
                          <span className={`block truncate text-[15px] font-bold ${dl?.tone === "overdue" ? "text-rose-700" : dl?.tone === "today" ? "text-amber-700" : "text-slate-900"}`}>
                            {task.deadline ? `${fmtCz(task.deadline)}${dl && dl.tone !== "future" ? `, ${dl.text}` : ""}` : "Bez termínu"}
                          </span>
                        </span>
                        {smiDeadline && (
                          <input type="date" aria-label="Termín" className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                            defaultValue={task.deadline}
                            onChange={(e) => e.target.value !== task.deadline && saveField({ deadline: e.target.value })} />
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="relative flex min-h-[64px] items-center gap-2.5 px-4 py-2">
                    {col.ikona && <col.ikona className="h-5 w-5 shrink-0 text-slate-400" />}
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Sloupec</span>
                      <span className="block truncate text-[15px] font-bold text-slate-900">{col.label}</span>
                    </span>
                    <select aria-label="Sloupec" className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      value={task.status} onChange={(e) => post({ id: taskId, status: e.target.value })}>
                      {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="relative flex min-h-[64px] items-center gap-2.5 px-4 py-2">
                    <prio.icon className={`h-5 w-5 shrink-0 ${task.priority === "High" ? "text-rose-500" : task.priority === "Low" ? "text-slate-400" : "text-amber-500"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Priorita</span>
                      <span className={`block truncate text-[15px] font-bold ${task.priority === "High" ? "text-rose-700" : "text-slate-900"}`}>{prio.label}</span>
                    </span>
                    <select aria-label="Priorita" className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      value={task.priority || "Medium"} onChange={(e) => saveField({ priority: e.target.value })}>
                      {Object.keys(PRIORITY_META).map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                    </select>
                  </div>
                </div>
                {/* Zbytek vlastností (kategorie, projekt, kontrola, viditelnost) je
                    za jedním řádkem, ať nezabírají celou obrazovku. */}
                <button type="button" onClick={() => setVlastnostiOtevrene((v) => !v)}
                  className="flex min-h-[52px] w-full items-center gap-3 border-t border-slate-100 px-4 text-left">
                  <SlidersHorizontal className="h-5 w-5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-600">Kategorie, projekt, kontrola, viditelnost</span>
                  {vlastnostiOtevrene
                    ? <ChevronDown className="h-5 w-5 shrink-0 text-slate-400" />
                    : <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />}
                </button>
              </div>

              {/* Plný panel vlastností: na počítači vpravo, na telefonu se rozbalí
                  tlačítkem nad ním. */}
              <div className={`order-1 space-y-3 border-b border-slate-200 bg-slate-50/80 p-4 md:order-2 md:block md:space-y-3.5 md:border-b-0 md:border-l md:border-slate-100 ${vlastnostiOtevrene ? "" : "hidden"}`}>
                <div>
                  <div className={`${sideLabel} mb-1`}>Sloupec</div>
                  <select className={sideInput} value={task.status} onChange={(e) => post({ id: taskId, status: e.target.value })}>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <div className={`${sideLabel} mb-1`}>Priorita</div>
                  <select className={sideInput} value={task.priority || "Medium"} onChange={(e) => saveField({ priority: e.target.value })}>
                    {Object.keys(PRIORITY_META).map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                  </select>
                </div>
                <div>
                  <div className={`${sideLabel} mb-1`}>Pilot (kdo dělá)</div>
                  <div className="flex items-center gap-2">
                    <Avatar name={task.pilot} foto={fotoByName?.[task.pilot] || ""} className="w-7 h-7" textClassName="text-[11px]" />
                    <select className={sideInput} value={task.pilot || ""} onChange={(e) => saveField({ pilot: e.target.value })}>
                      <option value="">Nepřiřazeno</option>
                      {people.map((p) => <option key={p} value={p}>{p}</option>)}
                      {task.pilot && !people.includes(task.pilot) && <option value={task.pilot}>{task.pilot}</option>}
                    </select>
                  </div>
                </div>
                <div>
                  <div className={`${sideLabel} mb-1`}>Deadline</div>
                  {(() => {
                    const jeOwner = (getCurrentUser()?.role || "") === "owner";
                    const jeZadavatel = String(user?.name || "").toLowerCase() === String(task.zadavatel || "").toLowerCase();
                    const smiDeadline = jeOwner || jeZadavatel || !task.zadavatel;
                    return (
                      <>
                        <input type="date" className={`${sideInput} ${smiDeadline ? "" : "opacity-60 cursor-not-allowed"}`} defaultValue={task.deadline} disabled={!smiDeadline}
                          onBlur={(e) => e.target.value !== task.deadline && saveField({ deadline: e.target.value })} />
                        {!smiDeadline && <div className="text-[11px] text-slate-400 mt-0.5">Termín mění zadavatel ({task.zadavatel}) - napiš mu do komentáře.</div>}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <div className={`${sideLabel} mb-1`}>Kategorie</div>
                  <select className={sideInput} value={task.kategorie || ""} onChange={(e) => saveField({ kategorie: e.target.value })}>
                    <option value="">Bez kategorie</option>
                    {kategorie.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <div className={`${sideLabel} mb-1`}>Úroveň viditelnosti</div>
                  <div className="text-sm font-semibold text-slate-700 px-0.5">
                    {task.uroven === "basic" ? "Basic (vidí všichni)" : "Management"}
                    <span className="block text-[11px] font-normal text-slate-400">Odvozuje se z role pilota.</span>
                  </div>
                </div>
                {(detail.parent || (canManage && (detail.deti || []).length === 0)) && (
                  <div>
                    <div className={`${sideLabel} mb-1`}>Projekt</div>
                    {detail.parent ? (
                      <>
                        <button onClick={() => onOpenTask?.(detail.parent.id)}
                          className="w-full text-left text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl px-2.5 py-2 hover:bg-indigo-100 truncate">
                          ↳ {detail.parent.nazev}
                        </button>
                        {canManage && (
                          <button onClick={() => saveField({ parent_id: null })} className="text-[11px] text-slate-400 hover:text-rose-600 mt-0.5">Odpojit od projektu</button>
                        )}
                      </>
                    ) : (
                      <select className={sideInput} value={task.parent_id || ""} onChange={(e) => saveField({ parent_id: e.target.value || null })}>
                        <option value="">Samostatný úkol</option>
                        {(allTasks || []).filter((t) => t.id !== taskId && !t.parent_id && !["hotovo", "archiv"].includes(t.status)).map((t) => (
                          <option key={t.id} value={t.id}>↳ {t.nazev}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                {canManage && !task.parent_id && (
                  <label className="flex items-center gap-2 cursor-pointer bg-indigo-50/60 border border-indigo-100 rounded-xl px-2.5 py-2">
                    <input type="checkbox" checked={jeProjekt} disabled={deti.length > 0}
                      onChange={(e) => saveField({ je_projekt: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-300" />
                    <span className="text-xs">
                      <span className="font-bold text-indigo-800">Projekt</span>
                      <span className="text-indigo-700/80 block">{deti.length > 0 ? "Má navázané úkoly (projekt automaticky)." : "Sdružuje další úkoly, max 20."}</span>
                    </span>
                  </label>
                )}
                {(canManage || String(user?.name || "").toLowerCase() === String(task.zadavatel || "").toLowerCase()) && (
                  <label className="flex items-center gap-2 cursor-pointer bg-amber-50/60 border border-amber-100 rounded-xl px-2.5 py-2">
                    <input type="checkbox" checked={task.kontrola === true} onChange={(e) => saveField({ kontrola: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-300" />
                    <span className="text-xs">
                      <span className="font-bold text-amber-800">Kontrola</span>
                      <span className="text-amber-700/80 block">Jako hotový označí jen zadavatel{task.zadavatel ? ` (${task.zadavatel})` : ""}.</span>
                    </span>
                  </label>
                )}
                {canManage && (
                  <div>
                    <div className={`${sideLabel} mb-1`}>Tagy</div>
                    <TagInput value={task.tags} suggestions={tagSuggestions || []} onSave={(tags) => saveField({ tags })} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {/* Plný modal pro přidání úkolu do projektu (pilot, deadline, podúkoly…) */}
      {showAddChild && task && (
        <AddTaskModal people={people} tagSuggestions={tagSuggestions} kategorie={kategorie} canManage={canManage}
          defaultUroven={task.uroven} defaultParent={{ id: taskId, nazev: task.nazev }}
          onClose={() => setShowAddChild(false)} onCreated={() => { load(); onChanged(); }} />
      )}
      {!fullPage && textEditor && (
        <TextPrilohaEditor key={klicKonceptu} initialNazev={textEditor.nazev || ""} initialObsah={textEditor.obsah || ""} saving={savingText}
          draftKey={klicKonceptu} chyba={chybaText}
          onSave={saveText} onClose={() => { setChybaText(""); setTextEditor(null); }} />
      )}
      {!fullPage && textView && (
        <TextPrilohaViewer priloha={textView}
          canEdit={canManage || String(textView.autor || "").toLowerCase() === String(user?.email || "").toLowerCase()}
          onEdit={() => { setTextEditor({ prilohaId: textView.id, nazev: textView.nazev, obsah: textView.obsah }); setTextView(null); }}
          onDelete={() => { const id = textView.id; setTextView(null); deletePriloha(id); }}
          onToggleHotovo={() => prepniHotovo(textView)}
          onClose={() => setTextView(null)} />
      )}
    </div>
  );
  // Na celé stránce už neportálujeme: detail je běžný obsah stránky, takže
  // nad ním zůstává horní lišta i řada záložek a dá se přepnout jinam.
  // 10.9.2026: modal se vykresluje i uvnitř meetingu, kde ho karta s vlastním
  // overflow/backdrop-filter ořízne — `position: fixed` se pak počítá k ní, ne
  // k obrazovce. Portál do body je stejné řešení jako u panelu stopek.
  return fullPage ? modalContent : createPortal(modalContent, document.body);
}

// Modal pro nový úkol (redesign 17.7.2026): vše na jednom místě vč. podúkolů a tagů.
const modalLabel = "label";
// Export: modal se používá i mimo board (např. založení úkolu z meetingu s vazbou meeting_id).
// Sloupce, které mají termín ze své podstaty — tam je deadline povinný.
// Zásobník a Prioritní jsou fronty bez data, ty ho nepotřebují (zadání Lády 10.9.2026).
const DEADLINE_POVINNY = ["tydenni", "mesicni", "v_procesu"];
// Stejná pravidla jako na serveru: týden se plánuje čtvrtek → čtvrtek,
// měsíc končí posledním pátkem. Předvyplní se, člověk může přepsat.
function vychoziDeadline(status) {
  const d = new Date();
  if (status === "tydenni") {
    let posun = (4 - d.getDay() + 7) % 7;
    if (posun === 0) posun = 7;
    d.setDate(d.getDate() + posun);
  } else if (status === "mesicni") {
    d.setMonth(d.getMonth() + 1, 0);
    while (d.getDay() !== 5) d.setDate(d.getDate() - 1);
  } else return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function AddTaskModal({ people, tagSuggestions, kategorie, canManage, defaultUroven, defaultParent = null, defaultJeProjekt = false, defaultStatus = "tydenni", extraTaskFields = null, existingTasks = [], onClose, onCreated }) {
  const user = getCurrentUser();
  const [form, setForm] = useState({ nazev: "", popis: "", kategorie: "", pilot: "", deadline: vychoziDeadline(defaultStatus), priority: "Medium", status: defaultStatus, tags: "", checklistText: "", kontrola: false, je_projekt: defaultJeProjekt });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Úkoly projektu jako strukturované položky (název + pilot + deadline + priorita),
  // ne jen řádky textu — ať jdou detaily zadat rovnou.
  const [projTasks, setProjTasks] = useState([{ nazev: "", pilot: "", deadline: "", priority: "Medium" }]);
  const setProjTask = (i, k, v) => setProjTasks((list) => list.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addProjTask = () => setProjTasks((list) => (list.length >= 20 ? list : [...list, { nazev: "", pilot: form.pilot, deadline: "", priority: "Medium" }]));
  const removeProjTask = (i) => setProjTasks((list) => list.filter((_, idx) => idx !== i));
  // Do projektu jdou i PŘIPOJIT stávající úkoly z boardu (ne jen tvořit nové).
  const [attached, setAttached] = useState([]);
  const attachCandidates = useMemo(() => (existingTasks || []).filter((t) =>
    !["hotovo", "archiv"].includes(t.status) && !t.parent_id && !t.je_projekt &&
    !attached.some((a) => String(a.id) === String(t.id))
  ), [existingTasks, attached]);
  const pocetDetiCelkem = projTasks.filter((t) => t.nazev.trim()).length + attached.length;

  // Bod 7: autosave rozdelaneho ukolu do prohlizece (localStorage) — prezije pad PC / zavreni.
  // Jen hlavni tok (ne poduokol do projektu). Nabidne obnoveni pri znovuotevreni.
  const KONCEPT_KEY = "fw40_ukol_koncept_v1";
  const konceptPovoleno = !defaultParent;
  const [konceptNabidka, setKonceptNabidka] = useState(null);
  useEffect(() => {
    if (!konceptPovoleno) return;
    try {
      const raw = localStorage.getItem(KONCEPT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && d.form && (String(d.form.nazev || "").trim() || String(d.form.popis || "").trim())) setKonceptNabidka(d);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!konceptPovoleno) return;
    try {
      if (String(form.nazev || "").trim() || String(form.popis || "").trim()) {
        localStorage.setItem(KONCEPT_KEY, JSON.stringify({ form, projTasks, ts: new Date().toISOString() }));
      }
    } catch { /* ignore */ }
  }, [form, projTasks, konceptPovoleno]);
  const zahoditKoncept = () => { try { localStorage.removeItem(KONCEPT_KEY); } catch { /* ignore */ } setKonceptNabidka(null); };
  const obnovitKoncept = () => {
    if (!konceptNabidka) return;
    // Koncept vraci jen TEXT. Cilovy sloupec a rezim projektu urcuje tlacitko,
    // kterym se modal otevrel — jinak koncept z minule prepsal "Backlog" zpatky
    // na "Mesicni" a ukol skoncil jinde, nez kam ho clovek posilal (18.8.2026).
    if (konceptNabidka.form) {
      const { status: _s, je_projekt: _p, ...text } = konceptNabidka.form;
      setForm((f) => ({ ...f, ...text }));
    }
    if (Array.isArray(konceptNabidka.projTasks) && konceptNabidka.projTasks.length) setProjTasks(konceptNabidka.projTasks);
    setKonceptNabidka(null);
  };

  // Co ještě chybí, aby šel úkol založit. Prázdné pole = formulář je v pořádku.
  const chybiPole = useMemo(() => {
    const chybi = [];
    if (!form.nazev.trim()) chybi.push("název");
    if (!form.status) chybi.push("sloupec");
    if (!form.kategorie) chybi.push("kategorii");
    if (!form.pilot) chybi.push("pilota");
    if (!defaultParent && DEADLINE_POVINNY.includes(form.status) && !form.deadline) chybi.push("deadline");
    return chybi;
  }, [form.nazev, form.status, form.kategorie, form.pilot, form.deadline, defaultParent]);
  const chybiVeta = chybiPole.length <= 1
    ? chybiPole.join("")
    : `${chybiPole.slice(0, -1).join(", ")} a ${chybiPole[chybiPole.length - 1]}`;

  const submit = async (e) => {
    e.preventDefault();
    if (chybiPole.length) { setErr(`Doplň ${chybiVeta}.`); return; }
    setSaving(true);
    setErr("");
    try {
      const { checklistText, ...task } = form;
      const jeProjektMode = form.je_projekt && !defaultParent;
      const detiProjektu = jeProjektMode ? projTasks.filter((t) => t.nazev.trim()).slice(0, 20) : [];
      const radky = checklistText.split("\n").map((x) => x.trim()).filter(Boolean);
      const r = await apiFetch("/api/operations-ukoly", {
        method: "POST",
        body: JSON.stringify({
          action: "create",
          task: {
            ...task,
            parent_id: defaultParent?.id || undefined,
            // U projektu se řádky založí jako navázané ÚKOLY (níže), ne checklist.
            checklist: jeProjektMode ? [] : radky,
            zadavatel: user?.name || user?.email || "",
            ...(extraTaskFields || {}),
          },
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Vytvoření selhalo");
      // Projekt: každá položka = samostatný úkol projektu i s detaily
      // (pilot, deadline, priorita). Max 20 hlídá backend.
      if (jeProjektMode && j.id) {
        for (const t of detiProjektu) {
          await apiFetch("/api/operations-ukoly", {
            method: "POST",
            body: JSON.stringify({ action: "create", task: {
              nazev: t.nazev.trim(), parent_id: j.id, status: form.status,
              pilot: t.pilot || form.pilot, deadline: t.deadline || form.deadline || "", priority: t.priority || "Medium",
              kategorie: form.kategorie, zadavatel: user?.name || user?.email || "",
            } }),
          }).catch(() => {});
        }
        // Připojení stávajících úkolů z boardu (parent_id na nový projekt).
        for (const t of attached) {
          await apiFetch("/api/operations-ukoly", {
            method: "POST",
            body: JSON.stringify({ id: String(t.id), fields: { parent_id: String(j.id) } }),
          }).catch(() => {});
        }
      }
      try { localStorage.removeItem(KONCEPT_KEY); } catch { /* ignore */ }
      onCreated();
      onClose();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto p-3 sm:p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <form className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-6 overflow-hidden" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className={`border-b flex items-center gap-3 px-5 py-4 ${defaultParent || form.je_projekt ? "bg-gradient-to-r from-indigo-50 to-white border-indigo-100" : "bg-gradient-to-r from-violet-50 to-white border-violet-100"}`}>
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${defaultParent || form.je_projekt ? "bg-indigo-100 text-indigo-700" : "bg-violet-100 text-violet-700"}`}>
            {defaultParent || form.je_projekt ? <FolderKanban className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          </span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900 leading-tight">{defaultParent ? "Nový úkol do projektu" : form.je_projekt ? "Nový projekt" : "Nový úkol"}</h3>
            {defaultParent && <div className="flex items-center gap-1 text-xs font-semibold text-brand-deep"><FolderOpen className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{defaultParent.nazev}</span></div>}
            <div className="text-xs text-slate-400">{form.je_projekt && !defaultParent ? "Sdružuje další úkoly (max 20) a ukazuje, kolik je splněno." : "Rovnou i s podúkoly a tagy, ať je hned kompletní."}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-white/80 min-h-[44px] sm:min-h-0"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="mx-5 mt-3 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl p-2">{err}</div>}
        <div className="p-5 space-y-4">
          {konceptNabidka && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-sm">
              <StickyNote className="w-5 h-5 shrink-0 text-amber-700" />
              <div className="flex-1 min-w-0 text-amber-800">Máš rozdělaný koncept{konceptNabidka.form?.nazev ? ` \u201e${konceptNabidka.form.nazev}\u201c` : ""}. Obnovit?</div>
              <button type="button" onClick={obnovitKoncept} className="btn btn-primary shrink-0">Obnovit</button>
              <button type="button" onClick={zahoditKoncept} className="shrink-0 text-xs font-bold text-amber-700 hover:text-amber-900">Zahodit</button>
            </div>
          )}
          <div>
            <label className={modalLabel}>Název *</label>
            <input className="field mt-1 text-base font-semibold" placeholder="Co je potřeba udělat?" value={form.nazev} onChange={set("nazev")} autoFocus />
          </div>
          <div>
            <label className={modalLabel}>Popis</label>
            <textarea className="field mt-1 min-h-[70px]" placeholder="Detaily, podklady, souvislosti…" value={form.popis} onChange={set("popis")} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={modalLabel}>Sloupec *</label>
              <select className="field mt-1" value={form.status}
                onChange={(e) => {
                  const status = e.target.value;
                  // Termín se předvyplní podle sloupce, ať ho nemusí každý ťukat ručně.
                  setForm((f) => ({ ...f, status, deadline: f.deadline || vychoziDeadline(status) }));
                }}>
                {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={modalLabel}>Priorita</label>
              <select className="field mt-1" value={form.priority} onChange={set("priority")}>
                {Object.keys(PRIORITY_META).map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
              </select>
            </div>
            <div>
              <label className={modalLabel}>Kategorie *</label>
              <select className={`field mt-1 ${form.kategorie ? "" : "border-amber-300"}`} value={form.kategorie} onChange={set("kategorie")}>
                <option value="">Vyber kategorii…</option>
                {kategorie.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className={modalLabel}>Pilot (kdo dělá) *</label>
              <select className={`field mt-1 ${form.pilot ? "" : "border-amber-300"}`} value={form.pilot} onChange={set("pilot")}>
                <option value="">Vyber, kdo to udělá…</option>
                {people.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={modalLabel}>Deadline{!defaultParent && DEADLINE_POVINNY.includes(form.status) ? " *" : ""}</label>
              <input type="date" className={`field mt-1 ${!defaultParent && DEADLINE_POVINNY.includes(form.status) && !form.deadline ? "border-amber-300" : ""}`} value={form.deadline} onChange={set("deadline")} />
              {!form.deadline && !defaultParent && (
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {DEADLINE_POVINNY.includes(form.status)
                    ? "U tohohle sloupce je termín povinný."
                    : "Zásobník a Prioritní termín mít nemusí."}
                </div>
              )}
            </div>

          </div>
          {form.je_projekt && !defaultParent ? (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <FolderKanban className="w-4 h-4 text-indigo-500" />
                <label className="text-sm font-bold text-slate-800">Úkoly projektu</label>
                <span className="text-[11px] text-slate-400 tabular-nums">{pocetDetiCelkem}/20, založí se jako samostatné úkoly</span>
              </div>
              <div className="space-y-2">
                {projTasks.map((t, i) => (
                  <div key={i} className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-2.5 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-indigo-400 tabular-nums w-4 text-center shrink-0">{i + 1}.</span>
                      <input className="field font-medium" placeholder="Název úkolu…" value={t.nazev}
                        onChange={(e) => setProjTask(i, "nazev", e.target.value)} />
                      {projTasks.length > 1 && (
                        <button type="button" onClick={() => removeProjTask(i)} className="p-1.5 text-slate-400 hover:text-rose-600 shrink-0" title="Odebrat úkol"><X className="w-4 h-4" /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-6">
                      <select className="field text-xs py-1.5" value={t.pilot} onChange={(e) => setProjTask(i, "pilot", e.target.value)} title="Pilot (kdo dělá)">
                        <option value="">Pilot: jako projekt</option>
                        {people.map((pp) => <option key={pp} value={pp}>{pp}</option>)}
                      </select>
                      <input type="date" className="field text-xs py-1.5" value={t.deadline} onChange={(e) => setProjTask(i, "deadline", e.target.value)} title="Deadline úkolu" />
                      <select className="field text-xs py-1.5 col-span-2 sm:col-span-1" value={t.priority} onChange={(e) => setProjTask(i, "priority", e.target.value)} title="Priorita">
                        {Object.keys(PRIORITY_META).map((pr) => <option key={pr} value={pr}>{PRIORITY_META[pr].label}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addProjTask} disabled={pocetDetiCelkem >= 20 || projTasks.length >= 20}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl px-2.5 py-1.5 hover:bg-indigo-100 disabled:opacity-50">
                <Plus className="w-3.5 h-3.5" /> Přidat další úkol
              </button>
              {/* Připojit stávající úkoly z boardu do projektu */}
              <div className="mt-3 pt-3 border-t border-indigo-100">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Link2 className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-[11px] font-semibold text-slate-500">Nebo připoj stávající úkoly z boardu</span>
                </div>
                {attached.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {attached.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 bg-indigo-50/40 border border-indigo-100 rounded-xl px-2.5 py-1.5">
                        <ListChecks className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        <span className="text-sm text-slate-800 truncate flex-1">{t.nazev}</span>
                        {t.pilot && <span className="text-[11px] text-slate-400 shrink-0 demo-sensitive">{t.pilot}</span>}
                        <button type="button" onClick={() => setAttached((a) => a.filter((x) => String(x.id) !== String(t.id)))}
                          className="p-0.5 rounded text-slate-400 hover:text-rose-600 shrink-0" title="Odebrat z projektu"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {pocetDetiCelkem < 20 && attachCandidates.length > 0 && (
                  <UkolPicker tasks={attachCandidates} value=""
                    onChange={(id) => { const t = attachCandidates.find((x) => String(x.id) === String(id)); if (t) setAttached((a) => [...a, t]); }}
                    placeholder="Najít a připojit existující úkol…" className="w-full" />
                )}
              </div>
            </div>
          ) : (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ListChecks className="w-4 h-4 text-violet-500" />
              <label className="text-sm font-bold text-slate-800">Podúkoly</label>
              <span className="text-[11px] text-slate-400">jeden na řádek, úkol jde uzavřít až po odškrtání všech</span>
            </div>
            <textarea className="field min-h-[80px] font-mono text-[13px]" placeholder={"Vytáhnout seznam z appky\nObvolat klientky\nZapsat výsledky"} value={form.checklistText} onChange={set("checklistText")} />
          </div>
          )}
          <div>
            <label className={modalLabel}>Tagy</label>
            <div className="mt-1">
              <TagInput value={form.tags} suggestions={tagSuggestions || []} onSave={(tags) => setForm((f) => ({ ...f, tags }))} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.kontrola} onChange={(e) => setForm((f) => ({ ...f, kontrola: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-300" />
            <span className="text-sm text-slate-700"><span className="font-semibold">Kontrola</span> - jako hotový úkol označíš jen ty (zadavatel)</span>
          </label>

        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-slate-50/60">
          <button type="button" onClick={onClose} className={btnGhost}>Zrušit</button>
          {chybiPole.length > 0 && <span className="text-xs font-semibold text-amber-700 mr-auto">Chybí: {chybiVeta}</span>}
          <button type="submit" disabled={saving || chybiPole.length > 0} className="btn btn-primary">{form.je_projekt && !defaultParent ? "Vytvořit projekt" : "Vytvořit úkol"}</button>
        </div>
      </form>
    </div>
  ), document.body);
}

// Panel rutin (opakované úkoly, jen owner). Rozbalovací, editace v modalu.
// 2. 11. 2026: CSM (sPotvrzenim) zakládá a upravuje rutiny rovnou bez schválení ownerem,
// jen se zaškrtnutým potvrzením osobní kontroly (hlídá i server).
// 3. 11. 2026 (zadání ownera): rutina může mít jako pilota trenéra (pilot_typ trener). V termínu
// založí úkol do jeho Úkolů (trener_ukoly), ne do tohoto boardu. Zakládá a upravuje owner, CSM
// a manažer, CSM a manažer rovnou s potvrzením osobní kontroly (trenerSPotvrzenim).
function RutinyPanel({ rutiny, people, fotoByName, kategorie, onChanged, isOwner = false, sPotvrzenim = false, treneri = [], trenerSPotvrzenim = false }) {
  // Filtry rutin (pilot, frekvence) + denní Operations rutiny sbalené do dropdownu.
  const [filtrPilot, setFiltrPilot] = useState("vse");
  const [filtrFrek, setFiltrFrek] = useState("vse");
  const [opsOpen, setOpsOpen] = useState(false);
  const [filtrPilotOpen, setFiltrPilotOpen] = useState(false);
  const filtrPilotRef = useRef(null);
  const rutinyPiloti = [...new Set(rutiny.map((r) => r.pilot).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs"));
  const filtrovane = rutiny.filter((r) =>
    (filtrPilot === "vse" || r.pilot === filtrPilot) &&
    (filtrFrek === "vse" || r.frekvence === filtrFrek));
  const jeOpsDen = (r) => /^Operations (pondělí|úterý|středa|čtvrtek|pátek)/.test(String(r.nazev || ""));
  const opsDny = filtrovane.filter(jeOpsDen).sort((a, b) => (a.deadline_den || 9) - (b.deadline_den || 9));
  const zobrazene = filtrovane.filter((r) => !jeOpsDen(r));

  const jaJmeno = String(getCurrentUser()?.name || "").toLowerCase();
  // Tolerantní shoda pilot rutiny vs. moje jméno (Tinka vs Kristina Žatečková).
  const jsemPilot = (pilot) => {
    const p = String(pilot || "").toLowerCase(), n = jaJmeno;
    if (!p || !n) return false;
    return p === n || p.includes(n) || n.includes(p) || p.split(/\s+/)[0] === n.split(/\s+/)[0];
  };
  // CSM upravuje schválené rutiny a svůj vlastní návrh, cizí čekající návrh schvaluje owner (server vrací 409).
  const jaNavrhovatel = String(getCurrentUser()?.name || getCurrentUser()?.email || "").trim().toLowerCase();
  const mujNavrh = (r) => !!jaNavrhovatel && String(r.navrhl || "").trim().toLowerCase() === jaNavrhovatel;
  const [open, setOpen] = useState(false);
  const [editRutina, setEditRutina] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const boxRef = useRef(null);

  const post = async (payload) => {
    const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify(payload) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast(j.error || "Akce selhala", "error"); return; }
    onChanged();
  };

  // Jedna řádka rutiny (sdílená pro hlavní seznam i dropdown Operations dnů).
  const radekRutiny = (r) => (
            <div key={r.id} className={`flex items-center gap-3 rounded-xl border p-3 ${r.aktivni ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100"}`}>
              <Avatar name={r.pilot} foto={fotoByName?.[r.pilot] || ""} className="w-8 h-8" textClassName="text-[11px]" />
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-semibold truncate ${r.aktivni ? "text-slate-800" : "text-slate-400 line-through"}`}>{r.nazev}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-slate-500 truncate demo-sensitive">{r.pilot}</span>
                  {r.pilot_typ === "trener" && <Stitek velikost="mala" ton="info" ikona={Dumbbell}>trenér, do jeho Úkolů</Stitek>}
                  {r.kategorie && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${KATEGORIE_BARVA[r.kategorie] || "bg-slate-100 text-slate-600"}`}>{r.kategorie}</span>}
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">{r.frekvence === "tydenni" ? "každý týden" : r.frekvence === "dvoumesicni" ? "co 2 měsíce" : r.frekvence === "svatky" ? "3 týdny před svátkem" : r.frekvence === "rocni" ? `každý rok, ${MESICE_RUTINY[(r.mesic || 1) - 1] || "?"}` : "každý měsíc"}</span>
                  {r.priorita === "High" && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100">vysoká priorita</span>}
                  {/* Kdy se úkol tvoří a dokdy má být hotový — na první pohled. */}
                  {r.frekvence === "svatky" && (
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">deadline den před svátkem</span>
                  )}
                  {r.frekvence !== "tydenni" && r.frekvence !== "svatky" && r.zadat_od_dne && (
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100">vytvořit {r.zadat_od_dne}. den</span>
                  )}
                  {r.deadline_den && (
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                      {r.frekvence === "tydenni" ? `deadline ${["Po", "Út", "St", "Čt", "Pá", "So", "Ne"][r.deadline_den - 1] || "?"}` : `deadline ${r.deadline_den}. den`}
                    </span>
                  )}
                  {Array.isArray(r.checklist) && r.checklist.length > 0 && (
                    <span className="text-[11px] text-slate-400 inline-flex items-center gap-0.5"><ListChecks className="w-3 h-3" />{r.checklist.length}</span>
                  )}
                  {r.zkontroloval && <PilulkaKontroly rutina={r} />}
                </div>
              </div>
              {r.schvaleno === false && (
                <span className="text-[11px] font-bold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 shrink-0" title={`Navrhl(a) ${r.navrhl || "?"} - ${r.uprava_rutiny_id ? "úprava se projeví po schválení ownerem" : "negeneruje se, dokud owner neschválí"}`}>
                  <Hourglass className="mr-1 inline-block w-3 h-3 align-[-1px]" />{r.uprava_rutiny_id ? "návrh úpravy" : "čeká na schválení"}
                </span>
              )}
              {/* Ruční založení instance mimo plán: owner nebo pilot rutiny. */}
              {r.schvaleno !== false && (isOwner || (r.pilot_typ === "trener" ? trenerSPotvrzenim : jsemPilot(r.pilot))) && (
                <button onClick={async () => { await post({ action: "rutinaSpustit", id: r.id }); toast(r.pilot_typ === "trener" ? `Úkol „${r.nazev}" založen trenérovi ${r.pilot} do jeho Úkolů.` : `Úkol „${r.nazev}" založen ve sloupci Týdenní.`, "success"); }}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-xl px-2.5 py-1.5 hover:bg-violet-100"
                  title="Založí úkol z této rutiny hned teď, mimo plánovanou automatizaci">
                  <Play className="w-3.5 h-3.5" /> Zadat teď
                </button>
              )}
              {isOwner && r.schvaleno === false && (
                <button onClick={() => post({ action: "rutinaSchvalit", id: r.id })}
                  className="btn btn-primary shrink-0"><Check className="w-3.5 h-3.5" /> Schválit</button>
              )}
              {isOwner ? (
                <>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!r.aktivni}
                    onClick={() => post({ action: "rutinaUpdate", id: r.id, fields: { aktivni: !r.aktivni } })}
                    title={r.aktivni ? "Rutina je zapnutá, kliknutím vypneš" : "Rutina je vypnutá, kliknutím zapneš"}
                    className="inline-flex items-center gap-1.5 shrink-0"
                  >
                    <span className={`relative inline-flex w-9 h-5 rounded-full transition-colors duration-200 ${r.aktivni ? "bg-violet-600" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${r.aktivni ? "translate-x-4" : ""}`} />
                    </span>
                    <span className={`text-xs ${r.aktivni ? "text-violet-700 font-medium" : "text-slate-400"}`}>{r.aktivni ? "zapnuto" : "vypnuto"}</span>
                  </button>
                  <button onClick={() => setEditRutina(r)} className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Upravit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setPendingDelete(r)} className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50" title="Smazat"><Trash2 className="w-4 h-4" /></button>
                </>
              ) : (sPotvrzenim || (trenerSPotvrzenim && r.pilot_typ === "trener")) ? (
                !r.uprava_rutiny_id && r.frekvence !== "svatky" && (r.schvaleno !== false || mujNavrh(r)) ? (
                  <button onClick={() => setEditRutina(r)} className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Upravit (bez schválení, po tvé kontrole)"><Pencil className="w-4 h-4" /></button>
                ) : (
                  <span className="shrink-0 text-slate-300" title="Rutiny schvaluje owner"><Lock className="w-4 h-4" /></span>
                )
              ) : (
                r.schvaleno !== false && !r.uprava_rutiny_id ? (
                  <button onClick={() => setEditRutina(r)} className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Navrhnout úpravu (schválí owner)"><Pencil className="w-4 h-4" /></button>
                ) : (
                  <span className="shrink-0 text-slate-300" title="Rutiny schvaluje owner"><Lock className="w-4 h-4" /></span>
                )
              )}
            </div>
  );

  return (
    <div ref={boxRef} className="glass scroll-mt-20 overflow-hidden">
      <button onClick={() => setOpen((o) => { const n = !o; if (n) setTimeout(() => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); return n; })}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors">
        <span className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0"><Repeat className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-800">Rutiny (opakované úkoly)</div>
          <div className="text-xs text-slate-400 truncate">Každý týden nebo měsíc samy založí úkol do sloupce Týdenní, checklist se propíše do podúkolů.</div>
        </div>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-100">{rutiny.length}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
          {/* Filtry ve stejném designu jako nahoře na kanban boardu. */}
          <div className="flex flex-wrap items-center gap-2 pb-1">
            <div className="relative" ref={filtrPilotRef}>
              <button onClick={() => setFiltrPilotOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-sm border rounded-xl px-2.5 py-1.5 bg-white font-semibold ${filtrPilot !== "vse" ? "border-violet-400 text-violet-700" : "border-slate-300 text-slate-600"}`}>
                <UsersIcon className="w-4 h-4" />
                {filtrPilot === "vse" ? "Všichni piloti" : filtrPilot.split(" ")[0]}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <PlovouciNabidka kotva={filtrPilotRef} otevreno={filtrPilotOpen} onZavrit={() => setFiltrPilotOpen(false)} sirka={256} className="py-1.5">
                  <button onClick={() => { setFiltrPilot("vse"); setFiltrPilotOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-50 text-sm ${filtrPilot === "vse" ? "font-bold text-violet-700" : "text-slate-700"}`}>
                    Všichni piloti
                  </button>
                  {rutinyPiloti.map((pj) => (
                    <button key={pj} onClick={() => { setFiltrPilot(pj); setFiltrPilotOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-50 ${filtrPilot === pj ? "bg-violet-50" : ""}`}>
                      <Avatar name={pj} foto={fotoByName?.[pj] || ""} className="w-6 h-6" textClassName="text-[11px]" />
                      <span className={`text-sm truncate demo-sensitive ${filtrPilot === pj ? "font-bold text-violet-700" : "text-slate-700"}`}>{pj}</span>
                    </button>
                  ))}
                </PlovouciNabidka>
            </div>
            <select className={`text-sm border rounded-xl px-2.5 py-1.5 bg-white font-semibold ${filtrFrek !== "vse" ? "border-violet-400 text-violet-700" : "border-slate-300 text-slate-600"}`}
              value={filtrFrek} onChange={(e) => setFiltrFrek(e.target.value)}>
              <option value="vse">Všechny frekvence</option>
              <option value="tydenni">Týdenní</option>
              <option value="mesicni">Měsíční</option>
              <option value="dvoumesicni">Co 2 měsíce</option>
              <option value="rocni">Roční</option>
              <option value="svatky">Státní svátky</option>
            </select>
            {(filtrPilot !== "vse" || filtrFrek !== "vse") && (
              <button onClick={() => { setFiltrPilot("vse"); setFiltrFrek("vse"); }} className="text-xs font-semibold text-slate-400 hover:text-violet-600">zrušit filtry</button>
            )}
          </div>
          {zobrazene.map((r) => radekRutiny(r))}
          {opsDny.length > 0 && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 overflow-hidden">
              <button onClick={() => setOpsOpen((v) => !v)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-sky-50">
                <CalendarDays className="w-4 h-4 shrink-0 text-sky-600" />
                <span className="text-sm font-bold text-sky-900 flex-1">Operations po dnech (CSM)</span>
                <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">{opsDny.length}</span>
                {opsOpen ? <ChevronDown className="w-4 h-4 text-sky-500" /> : <ChevronRight className="w-4 h-4 text-sky-500" />}
              </button>
              {opsOpen && <div className="px-2 pb-2 space-y-2">{opsDny.map((r) => radekRutiny(r))}</div>}
            </div>
          )}
          {filtrovane.length === 0 && <div className="text-sm text-slate-400 text-center py-2">{rutiny.length === 0 ? "Zatím žádná rutina." : "Filtrům neodpovídá žádná rutina."}</div>}
          <button onClick={() => setEditRutina({})} className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-violet-700 border border-dashed border-violet-200 hover:bg-violet-50 rounded-xl py-2.5 transition-colors">
            <Plus className="w-4 h-4" /> {isOwner || sPotvrzenim ? "Přidat rutinu" : trenerSPotvrzenim ? "Přidat rutinu (pro trenéra rovnou, pro back office ji schválí owner)" : "Navrhnout rutinu (schválí owner, dostane zprávu od Jokyho)"}
          </button>
        </div>
      )}
      {editRutina !== null && (
        <RutinaModal rutina={editRutina} people={people} kategorie={kategorie} sPotvrzenim={sPotvrzenim && !isOwner}
          treneri={treneri} trenerSPotvrzenim={trenerSPotvrzenim && !isOwner}
          zamknoutTyp={!!editRutina.id && !isOwner && !sPotvrzenim}
          onClose={() => setEditRutina(null)}
          onSave={async (form) => {
            if (editRutina.id && (isOwner || sPotvrzenim || (trenerSPotvrzenim && editRutina.pilot_typ === "trener"))) await post({ action: "rutinaUpdate", id: editRutina.id, fields: form });
            else if (editRutina.id) await post({ action: "rutinaNavrhUpravy", id: editRutina.id, fields: form });
            else await post({ action: "rutinaCreate", rutina: form });
            setEditRutina(null);
          }} />
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Smazat rutinu?"
        description={pendingDelete ? `Rutina "${pendingDelete.nazev}" se smaže. Už vygenerované úkoly zůstávají.` : ""}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => { post({ action: "rutinaDelete", id: pendingDelete.id }); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

// 2. 11. 2026: pilulka „Zkontrolovala Kristina Ž., 2. 11.“ u rutiny, kterou potvrdila CSM.
function zkratJmeno(jmeno) {
  const casti = String(jmeno || "").trim().split(/\s+/).filter(Boolean);
  if (casti.length < 2) return casti[0] || "";
  return `${casti[0]} ${casti[casti.length - 1].charAt(0)}.`;
}
function popisKontroly(r) {
  const d = r.zkontrolovano_at ? new Date(r.zkontrolovano_at) : null;
  const kdy = d && Number.isFinite(d.getTime())
    ? d.toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", timeZone: "Europe/Prague" }).replace(/\s/g, " ")
    : "";
  return `Zkontrolovala ${zkratJmeno(r.zkontroloval)}${kdy ? `, ${kdy}` : ""}`;
}
function PilulkaKontroly({ rutina, velikost = "mala" }) {
  return (
    <Stitek velikost={velikost} ton="ok" ikona={CheckCircle2}>
      <span className="demo-sensitive">{popisKontroly(rutina)}</span>
    </Stitek>
  );
}

// 31.10.2026: měsíce pro roční rutiny (zadání Kristiny, Vánoční pauza).
const MESICE_RUTINY = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

function RutinaModal({ rutina, people, kategorie, onClose, onSave, sPotvrzenim = false, treneri = [], trenerSPotvrzenim = false, zamknoutTyp = false }) {
  const [form, setForm] = useState({
    nazev: rutina.nazev || "",
    popis: rutina.popis || "",
    pilot_typ: rutina.pilot_typ === "trener" ? "trener" : "back_office",
    pilot: rutina.pilot || "",
    kategorie: rutina.kategorie || "",
    frekvence: rutina.frekvence || "tydenni",
    deadline_den: rutina.deadline_den || "",
    zadat_od_dne: rutina.zadat_od_dne || "",
    mesic: rutina.mesic || "",
    priorita: rutina.priorita || "Medium",
    checklistText: (rutina.checklist || []).join("\n"),
  });
  // 2. 11. 2026: CSM musí před uložením potvrdit osobní kontrolu (pokaždé znovu).
  const [zkontrolovano, setZkontrolovano] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // 3. 11. 2026: pilot trenér. Potvrzení kontroly: CSM vždy, manažer u rutiny pro trenéra.
  const proTrenera = form.pilot_typ === "trener";
  const jmenaTreneru = treneri.map((t) => t.name).filter(Boolean);
  const moznostiPilota = proTrenera ? jmenaTreneru : people;
  const potvrdit = sPotvrzenim || (trenerSPotvrzenim && proTrenera);
  const textPotvrzeni = sPotvrzenim ? "Rutinu jsem osobně zkontrolovala a je v pořádku" : "Rutinu jsem osobně zkontroloval(a) a je v pořádku";
  const navrhManazera = !sPotvrzenim && trenerSPotvrzenim && !proTrenera;
  const prepniTyp = (typ) => setForm((f) => (f.pilot_typ === typ ? f : { ...f, pilot_typ: typ, pilot: "" }));
  // 1. 11. 2026: okno se vykresluje portálem do body. Box Rutiny má třídu .glass
  // (backdrop-filter), a ta pro position: fixed vytváří vlastní rámec, takže se
  // okno na mobilu otevíralo jen uvnitř boxu místo přes celou obrazovku.
  return createPortal((
    <div className="fixed inset-0 z-50 bg-brand-dark/50 flex items-start justify-center overflow-y-auto p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <form className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.nazev.trim()) return;
          if (form.frekvence === "rocni" && !form.mesic) { toast("U roční rutiny vyber měsíc", "error"); return; }
          if (proTrenera && !form.pilot) { toast("Vyber trenéra, kterému bude rutina zakládat úkoly", "error"); return; }
          if (potvrdit && !zkontrolovano) { toast(sPotvrzenim ? "Nejdřív potvrď, že jsi rutinu osobně zkontrolovala" : "Nejdřív potvrď, že jsi rutinu osobně zkontroloval(a)", "error"); return; }
          onSave({
            ...(potvrdit ? { zkontrolovano: true } : {}),
            nazev: form.nazev, popis: form.popis, pilot: form.pilot, pilot_typ: proTrenera ? "trener" : "back_office", kategorie: form.kategorie,
            frekvence: form.frekvence, deadline_den: form.deadline_den || null,
            zadat_od_dne: form.frekvence === "tydenni" ? null : (form.zadat_od_dne || null),
            mesic: form.frekvence === "rocni" ? (form.mesic || null) : null,
            priorita: form.priorita || "Medium",
            checklist: form.checklistText.split("\n").map((x) => x.trim()).filter(Boolean),
          });
        }}>
        <div className="flex items-center gap-2 p-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900 flex-1">{rutina.id ? "Upravit rutinu" : "Nová rutina"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {rutina.zkontroloval && <div><PilulkaKontroly rutina={rutina} velikost="bezna" /></div>}
          {/* 3. 11. 2026: komu rutina zakládá úkoly. Trenér = úkol do jeho Úkolů, ne do tohoto boardu. */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500">Pilot je</label>
            {zamknoutTyp || form.frekvence === "svatky" ? (
              <div><Stitek ton={proTrenera ? "info" : "neutralni"} ikona={proTrenera ? Dumbbell : UsersIcon}>{proTrenera ? "Trenér, úkol do jeho Úkolů" : "Back office, úkol do tohoto boardu"}</Stitek></div>
            ) : (
              <Segment aria-label="Typ pilota rutiny" value={form.pilot_typ} onChange={prepniTyp}
                items={[{ key: "back_office", label: "Back office" }, { key: "trener", label: "Trenér" }]} />
            )}
            {proTrenera && (
              <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <Dumbbell className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>V termínu se trenérovi založí úkol do jeho Úkolů se stejnými poli jako úkol od manažera: termín, priorita, kategorie jako štítek a checklist. Trenér ho nesmaže ani neupraví.</span>
              </div>
            )}
            {navrhManazera && !rutina.id && (
              <div className="text-[11px] text-slate-500">Rutinu pro back office schválí owner, do té doby se nezakládá. Rutinu pro trenéra uložíš rovnou po své kontrole.</div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Název *</label>
            <input className="field" value={form.nazev} onChange={set("nazev")} autoFocus />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Popis</label>
            <textarea className="field min-h-[60px]" value={form.popis} onChange={set("popis")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">{proTrenera ? "Trenér *" : "Pilot"}</label>
              <select className="field" value={form.pilot} onChange={set("pilot")}>
                <option value="">{proTrenera ? "Vyber trenéra" : "—"}</option>
                {moznostiPilota.map((p) => <option key={p} value={p}>{p}</option>)}
                {form.pilot && !moznostiPilota.includes(form.pilot) && <option value={form.pilot}>{form.pilot}</option>}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Kategorie</label>
              <select className="field" value={form.kategorie} onChange={set("kategorie")}>
                <option value="">—</option>
                {kategorie.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Frekvence</label>
              {form.frekvence === "svatky" ? (
                <div className="text-sm text-slate-600 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                  Státní svátky, 3 týdny předem
                  <div className="text-[11px] text-slate-400 mt-0.5">Termín řídí seznam státních svátků, měnit ho tady nejde. Upravit můžeš pilota a checklist.</div>
                </div>
              ) : (
                <select className="field" value={form.frekvence} onChange={set("frekvence")}>
                  <option value="tydenni">Týdenní</option>
                  <option value="mesicni">Měsíční</option>
                  <option value="dvoumesicni">Jednou za 2 měsíce (sudé)</option>
                  <option value="rocni">Jednou za rok (vybraný měsíc)</option>
                </select>
              )}
            </div>
            {form.frekvence === "rocni" && (
              <div>
                <label className="text-xs font-semibold text-slate-500">Měsíc</label>
                <select className="field" value={form.mesic} onChange={set("mesic")}>
                  <option value="">Vyber měsíc</option>
                  {MESICE_RUTINY.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
            )}
            <div className={form.frekvence === "svatky" ? "hidden" : ""}>
              <label className="text-xs font-semibold text-slate-500">Priorita úkolu</label>
              <select className="field" value={form.priorita} onChange={set("priorita")}>
                <option value="High">Vysoká</option>
                <option value="Medium">Střední</option>
                <option value="Low">Nízká</option>
              </select>
            </div>
            <div className={form.frekvence === "svatky" ? "hidden" : ""}>
              <label className="text-xs font-semibold text-slate-500">Deadline</label>
              {form.frekvence === "tydenni" ? (
                <select className="field" value={form.deadline_den} onChange={set("deadline_den")}>
                  <option value="">Neděle (výchozí)</option>
                  {["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"].map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
                </select>
              ) : (
                <select className="field" value={form.deadline_den} onChange={set("deadline_den")}>
                  <option value="">{form.frekvence === "rocni" ? "Konec zvoleného měsíce (výchozí)" : "Konec měsíce (výchozí)"}</option>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}. den</option>)}
                </select>
              )}
            </div>
          </div>
          {form.frekvence !== "tydenni" && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Vytvořit úkol od dne</label>
              <select className="field" value={form.zadat_od_dne} onChange={set("zadat_od_dne")}>
                <option value="">Od začátku měsíce (výchozí)</option>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}. den v měsíci</option>)}
              </select>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Úkol se založí až od tohoto dne (např. vytvořit 18. s deadlinem 20.). Když je deadline den MENŠÍ než den vytvoření, spadne deadline do dalšího měsíce (příprava na meeting: vytvořit 24., deadline 1.).
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500">Checklist (jeden bod na řádek)</label>
            <textarea className="field min-h-[80px]" value={form.checklistText} onChange={set("checklistText")} />
          </div>
          {potvrdit && (
            <label className={`flex items-start gap-2.5 cursor-pointer rounded-xl border px-3 py-2.5 ${zkontrolovano ? "bg-emerald-50/70 border-emerald-200" : "bg-amber-50/60 border-amber-200"}`}>
              <input type="checkbox" checked={zkontrolovano} onChange={(e) => setZkontrolovano(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-300" />
              <span className="text-xs">
                <span className={`font-bold ${zkontrolovano ? "text-emerald-800" : "text-amber-800"}`}>{textPotvrzeni}</span>
                <span className={`block ${zkontrolovano ? "text-emerald-700/80" : "text-amber-700/80"}`}>Bez schválení ownerem začne platit hned po uložení. Uloží se, kdo a kdy ji zkontroloval.</span>
              </span>
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
          <button type="button" onClick={onClose} className={btnGhost}>Zrušit</button>
          <button type="submit" className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed" disabled={potvrdit && !zkontrolovano}>{navrhManazera && !rutina.id ? "Odeslat ke schválení" : navrhManazera ? "Navrhnout úpravu" : "Uložit"}</button>
        </div>
      </form>
    </div>
  ), document.body);
}

// Panel nastavení Slack agenta (jen owner). Klíče se ukládají do PG (app_config),
// protože Netlify env je na 4KB Lambda limitu. Zobrazuje maskované hodnoty,
// uloží jen nově vyplněné, umí otestovat připojení (auth.test).
function SlackAgentPanel() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null); // GET /slack-ukol-config
  const [form, setForm] = useState({ slack_bot_token: "", slack_signing_secret: "", groq_api_key: "" });
  const [znalosti, setZnalosti] = useState("");
  const [znalostiDirty, setZnalostiDirty] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch("/api/slack-ukol-config");
      const j = await r.json();
      if (r.ok) { setStatus(j); setZnalosti(j.znalosti || ""); setZnalostiDirty(false); }
    } catch { /* panel je volitelný */ }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const values = {};
      for (const k of Object.keys(form)) if (form[k].trim()) values[k] = form[k].trim();
      if (!Object.keys(values).length) { setMsg({ ok: false, text: "Nic k uložení, vyplň aspoň jedno pole." }); return; }
      const r = await apiFetch("/api/slack-ukol-config", { method: "POST", body: JSON.stringify({ action: "save", values }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      setForm({ slack_bot_token: "", slack_signing_secret: "", groq_api_key: "" });
      setMsg({ ok: true, text: "Uloženo." });
      load();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const saveZnalosti = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await apiFetch("/api/slack-ukol-config", { method: "POST", body: JSON.stringify({ action: "saveZnalosti", text: znalosti }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      setMsg({ ok: true, text: "Znalostní báze uložena." });
      setZnalostiDirty(false);
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await apiFetch("/api/slack-ukol-config", { method: "POST", body: JSON.stringify({ action: "test" }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Test selhal");
      const kusy = [`Připojeno k workspace ${j.team}.`];
      if (j.kanalyChyba) kusy.push(`Kanály se nepodařilo načíst (${j.kanalyChyba}).`);
      else kusy.push((j.clenKanalu || []).length ? `Joky je v kanálech: ${j.clenKanalu.join(", ")}.` : "Joky zatím není v žádném kanálu - pozvi ho přes /invite @joky.");
      if ((j.chybiScopes || []).length) kusy.push(`Chybí oprávnění: ${j.chybiScopes.join(", ")} - přidej je ve Slack aplikaci a dej Reinstall.`);
      else kusy.push("Všechna oprávnění v pořádku.");
      setMsg({ ok: true, text: kusy.join(" ") });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  // Diagnostika DM: krok za krokem projde cestu "jméno → email → Slack účet → DM"
  // a ukáže, kde přesně to selhává (proto piloti nedostávají zprávy).
  const [dmJmeno, setDmJmeno] = useState("");
  const [dmVysledek, setDmVysledek] = useState(null);
  const testDm = async () => {
    setBusy(true);
    setDmVysledek(null);
    try {
      const r = await apiFetch("/api/slack-ukol-config", { method: "POST", body: JSON.stringify({ action: "testDm", jmeno: dmJmeno.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Test selhal");
      setDmVysledek(j);
    } catch (e) { setDmVysledek({ ok: false, kroky: [{ krok: "Test", ok: false, detail: e.message }] }); }
    finally { setBusy(false); }
  };

  const hotovoVse = !!(status?.config?.slack_bot_token && status?.config?.slack_signing_secret);
  const StavPill = ({ on, label }) => (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-slate-300"}`} />
      {label}
    </span>
  );
  const fields = [
    { key: "slack_bot_token", label: "Slack bot token (xoxb-…)", hint: "Slack app > OAuth & Permissions" },
    { key: "slack_signing_secret", label: "Slack signing secret", hint: "Slack app > Basic Information" },
    { key: "groq_api_key", label: "Groq API klíč (hlasovky, zdarma)", hint: "console.groq.com > API Keys" },
  ];

  return (
    <div ref={boxRef} className="glass scroll-mt-20 overflow-hidden">
      <button onClick={() => setOpen((o) => { const n = !o; if (n) setTimeout(() => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); return n; })}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors">
        <span className="w-9 h-9 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-800">Slack agent Joky</div>
          <div className="text-xs text-slate-400 truncate">Zadávání úkolů hlasem i textem přes DM ve Slacku, schválení tlačítkem.</div>
        </div>
        {status && (
          <span className="hidden sm:flex items-center gap-1.5">
            <StavPill on={!!status.config?.slack_bot_token} label="token" />
            <StavPill on={!!status.config?.slack_signing_secret} label="podpis" />
            <StavPill on={!!status.config?.groq_api_key || !!status.config?.openai_api_key} label="hlasovky" />
          </span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
            Klíče se ukládají bezpečně v databázi. Uložené hodnoty se zobrazují maskované, vyplň jen to, co měníš.
            {hotovoVse ? " Agent je připojený - stačí napsat Jokymu ve Slacku." : " Po uložení otestuj připojení a napiš Jokymu ve Slacku."}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{f.label}</label>
                <input
                  type="password"
                  className="field mt-1"
                  placeholder={status?.config?.[f.key] ? `Uloženo: ${status.config[f.key]}` : f.hint}
                  value={form[f.key]}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
            ))}
          </div>
          {msg && (
            <div className={`text-sm rounded-xl px-3 py-2 border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
              {msg.text}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={busy} className="btn btn-primary">Uložit</button>
            <button onClick={test} disabled={busy || !status?.config?.slack_bot_token} className={`${btnGhost} border border-slate-200`}>Otestovat připojení</button>
            {status && !status.anthropic && (
              <span className="text-xs text-rose-600">Pozor: v Netlify chybí ANTHROPIC_API_KEY (koncepty nepůjdou).</span>
            )}
          </div>
          {/* Test DM pilotovi — proč nechodí "Nový úkol od..." zprávy */}
          <div className="pt-1 border-t border-slate-100">
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Test DM pilotovi</label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <input className="field !w-64" placeholder="Jméno přesně dle Nastavení > Uživatelé"
                value={dmJmeno} onChange={(e) => setDmJmeno(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && dmJmeno.trim()) testDm(); }} />
              <button onClick={testDm} disabled={busy || !dmJmeno.trim() || !status?.config?.slack_bot_token} className={`${btnGhost} border border-slate-200`}>
                Otestovat DM
              </button>
              <span className="text-[11px] text-slate-400">Projde celou cestu jméno → email → Slack a pošle testovací zprávu.</span>
            </div>
            {dmVysledek && (
              <div className={`mt-2 text-sm rounded-xl px-3 py-2 border space-y-1 ${dmVysledek.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                {(dmVysledek.kroky || []).map((k, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="shrink-0">{k.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-600" />}</span>
                    <span className="text-slate-700"><span className="font-semibold">{k.krok}:</span> {k.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="pt-1 border-t border-slate-100">
            <div className="flex items-baseline gap-2 mb-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Znalostní báze Jokyho</label>
              <span className="text-[11px] text-slate-400">{znalosti.length}/3000, čím kratší a konkrétnější, tím je Joky chytřejší</span>
            </div>
            <textarea
              className="field min-h-[90px] font-mono text-[12px]"
              placeholder={"- jídelníčky posíláme klientkám v pondělí\n- kampaně schvaluje Láďa\n(Joky se učí i ze Slacku: \"zapamatuj si, že...\")"}
              value={znalosti}
              maxLength={3000}
              onChange={(e) => { setZnalosti(e.target.value); setZnalostiDirty(true); }}
            />
            {znalostiDirty && <button onClick={saveZnalosti} disabled={busy} className="btn btn-primary mt-2">Uložit znalosti</button>}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Model</span>
            <select
              className="field w-auto"
              value={status?.model || "claude-haiku-4-5"}
              onChange={async (e) => {
                setBusy(true);
                try {
                  await apiFetch("/api/slack-ukol-config", { method: "POST", body: JSON.stringify({ action: "save", values: { slack_ukol_model: e.target.value } }) });
                  setMsg({ ok: true, text: "Model uložen." });
                  load();
                } catch { setMsg({ ok: false, text: "Uložení modelu selhalo." }); }
                finally { setBusy(false); }
              }}
            >
              <option value="claude-haiku-4-5">Haiku - nejlevnější (~10-30 hal/kolo), horší čeština</option>
              <option value="claude-sonnet-4-6">Sonnet - lepší čeština a úsudek (~3x dražší)</option>
            </select>
          </div>
          {/* Počítadlo nákladů se přesunulo do Systém → Náklady AI (celkové
              náklady + rozpad jídelníčky / úkoly / ostatní). */}
          <div className="pt-2 border-t border-slate-100 text-xs text-slate-500">
            Náklady na Jokyho najdeš v{" "}
            <Link to="/naklady-ai" className="font-semibold text-violet-600 hover:text-violet-700 underline underline-offset-2">Systém → Náklady AI</Link>
            {" "}(celkem i v rozpadu úkoly / jídelníčky).
          </div>
        </div>
      )}
    </div>
  );
}

export default function OperationsUkoly() {
  const { data, setData, loading, error, refetch } = useApiData("/api/operations-ukoly?includeArchive=1");
  const canManage = !!data?.canManage;
  const canDelete = !!data?.canDelete;
  const isOwner = data?.role === "owner";
  // Piloti: JEN back office lidé (uzivatele s rolí owner/manager/csm/sales/nutritionist),
  // trenéři mají vlastní board. Fotky z HR evidence dodává backend (lide[].foto).
  const lide = data?.lide || [];
  const people = useMemo(() => lide.map((l) => l.name).filter(Boolean), [lide]);
  const fotoByName = useMemo(() => {
    const m = {};
    for (const l of lide) if (l.name) m[l.name] = l.foto || "";
    // 3. 11. 2026: fotky trenérů pro rutiny s pilotem trenérem.
    for (const t of data?.treneriRutin || []) if (t.name && !m[t.name]) m[t.name] = t.foto || "";
    return m;
  }, [lide, data?.treneriRutin]);
  const tagSuggestions = useMemo(() => {
    const set = new Set();
    for (const t of data?.tasks || []) for (const tag of splitTags(t.tags)) if (!SYSTEM_TAGS.has(tag.toLowerCase())) set.add(tag);
    return [...set].sort((a, b) => a.localeCompare(b, "cs"));
  }, [data]);
  const kategorie = data?.kategorie || ["Marketing", "Operations", "HR", "Clients Management", "System"];

  // Úroveň boardu: management uživatel přepíná Management/Basic, basic role vidí jen basic.
  const [board, setBoard] = useState("management");
  const effectiveBoard = canManage ? board : "basic";

  const [filterKategorie, setFilterKategorie] = useState("vse");
  const [filterDeadline, setFilterDeadline] = useState("vse"); // vse | tyden | mesic | po
  const [filterProjekty, setFilterProjekty] = useState(false); // jen projekty s progressy
  // Filtr pilotů = MULTI výběr (checkboxy): CSM/manager má defaultně zaškrtnutou
  // sebe, může přihodit další členy nebo zaškrtnout všechny. Owner vidí vše.
  const mojeRole = String(getCurrentUser()?.role || "").toLowerCase();
  const mojeJmeno = useMemo(() => {
    const n = String(getCurrentUser()?.name || "").toLowerCase().trim();
    if (!n) return "";
    const hit = (data?.lide || []).map((l) => l.name).find((jm) => {
      const j = String(jm || "").toLowerCase();
      return j === n || j.includes(n) || n.includes(j) || j.split(/\s+/)[0] === n.split(/\s+/)[0];
    });
    return hit || "";
  }, [data]);
  const [filterPiloti, setFilterPiloti] = useState(null); // null = neinicializováno, [] = všichni
  // Dlouhé sloupce (Backlog se 70+ úkoly): zobrazit prvních 10, zbytek za "Zobrazit dalších".
  const [rozbaleneSloupce, setRozbaleneSloupce] = useState({});
  useEffect(() => {
    if (filterPiloti !== null) return;
    if (!data) return;
    // Default: CSM/manager → jen já; owner/basic → všichni.
    if ((mojeRole === "csm" || mojeRole === "manager") && mojeJmeno) setFilterPiloti([mojeJmeno]);
    else setFilterPiloti([]);
  }, [data, mojeJmeno, mojeRole, filterPiloti]);
  const aktivniPiloti = filterPiloti || [];
  const [search, setSearch] = useState("");
  // 4.9.2026 (fáze 4a): na telefonu se místo kanbanu ukazuje jeden seznam.
  // `seznam` je klíč sloupce boardu nebo pevné skupiny (kontrola/hotovo/archiv).
  const [seznam, setSeznam] = useState("tydenni");
  const [vyberOtevren, setVyberOtevren] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const ukolParam = searchParams.get("ukol");
  // FW-146: starší odkazy ze Slacku mají ?task=<id>. Převedeme je na jednotné
  // ?ukol=<id>, ať zavření detailu i další navigace fungují stejně.
  const taskParam = searchParams.get("task");
  useEffect(() => {
    if (!taskParam || ukolParam) return;
    setSearchParams((sp) => { sp.set("ukol", taskParam); sp.delete("task"); return sp; }, { replace: true });
  }, [taskParam, ukolParam, setSearchParams]);
  const [showAdd, setShowAdd] = useState(false);
  // 4.9.2026: v hlavičce je jedno +, po klepnutí se vybere úkol nebo projekt.
  // Dvě ikony vedle sebe nikomu neřekly, co je projekt a čím se liší.
  const [vyberNoveho, setVyberNoveho] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingDeleteUkazky, setPendingDeleteUkazky] = useState(false);
  const ukazkyCount = useMemo(
    () => (data?.tasks || []).filter((t) => t.zadavatel === "Ukázka" && t.tags === "ukazka").length,
    [data]
  );
  const doDeleteUkazky = useCallback(async () => {
    setPendingDeleteUkazky(false);
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ action: "deleteUkazky" }) });
      if (!r.ok) throw new Error("Smazání selhalo");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
  }, [refetch]);

  const today = todayIso();

  // Hranice pro filtr deadline: týden se plánuje čtvrtek → čtvrtek (týdenní meeting
  // je ve čtvrtek), takže okno = pátek–nejbližší čtvrtek, NE kalendářní týden do neděle.
  const deadlineOkna = useMemo(() => {
    const d = new Date(`${today}T12:00:00`);
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    const doCtvrtka = (4 - d.getDay() + 7) % 7; // 0 = dnes je čtvrtek → okno končí dnes
    const ct = new Date(d); ct.setDate(d.getDate() + doCtvrtka);
    const od = new Date(ct); od.setDate(ct.getDate() - 6);
    return { tydenOd: iso(od), tydenDo: iso(ct), mesic: today.slice(0, 7) };
  }, [today]);

  const matchesDeadline = useCallback((t) => {
    if (filterDeadline === "vse") return true;
    const d = String(t.deadline || "").slice(0, 10);
    if (!d) return false;
    if (filterDeadline === "po") return d < today && !["hotovo", "archiv"].includes(t.status);
    if (filterDeadline === "tyden") return d >= deadlineOkna.tydenOd && d <= deadlineOkna.tydenDo;
    if (filterDeadline === "mesic") return d.slice(0, 7) === deadlineOkna.mesic;
    return true;
  }, [filterDeadline, today, deadlineOkna]);

  const matchesFilter = useCallback((t) =>
    t.uroven === effectiveBoard &&
    (filterKategorie === "vse" || t.kategorie === filterKategorie) &&
    (aktivniPiloti.length === 0 || aktivniPiloti.includes(t.pilot)) &&
    (!filterProjekty || t.je_projekt === true || (t.deti_total || 0) > 0) &&
    matchesDeadline(t) &&
    (!search.trim() || `${t.nazev} ${t.popis} ${t.pilot}`.toLowerCase().includes(search.trim().toLowerCase())),
  [effectiveBoard, filterKategorie, aktivniPiloti, filterProjekty, matchesDeadline, search]);

  const byStatus = data?.byStatus || {};
  const sortTasks = (arr) => [...(arr || [])].filter(matchesFilter).sort((a, b) => {
    const pa = PRIO_ORDER[String(a.priority || "medium").toLowerCase()] ?? 2;
    const pb = PRIO_ORDER[String(b.priority || "medium").toLowerCase()] ?? 2;
    if (pa !== pb) return pa - pb;
    return String(a.deadline || "9999-12-31").localeCompare(String(b.deadline || "9999-12-31"));
  });

  // Hotovo/Archiv: řazení podle data dokončení + rychlé hledání podle jména/názvu.
  // "kontrola" = úkoly odevzdané ke kontrole (napříč sloupci), řazené dle odevzdání.
  const groupList = useMemo(() => {
    if (!openGroup) return [];
    let arr = openGroup === "kontrola"
      ? (data?.tasks || []).filter((t) => t.ke_kontrole_kdy && !["hotovo", "archiv"].includes(t.status)).filter(matchesFilter)
      : [...((data?.byStatus || {})[openGroup] || [])].filter(matchesFilter);
    const q = groupSearch.trim().toLowerCase();
    if (q) arr = arr.filter((t) => `${t.pilot} ${t.nazev} ${t.zadavatel}`.toLowerCase().includes(q));
    if (openGroup === "kontrola") return arr.sort((a, b) => String(b.ke_kontrole_kdy).localeCompare(String(a.ke_kontrole_kdy)));
    return arr.sort((a, b) => String(b.hotovo_kdy || "").localeCompare(String(a.hotovo_kdy || "")) || String(b.id).localeCompare(String(a.id)));
  }, [openGroup, data, groupSearch, matchesFilter]);

  const countFor = (uroven) => (data?.tasks || []).filter((t) => t.uroven === uroven && !["hotovo", "archiv"].includes(t.status)).length;

  // Kolik úkolů čeká na kontrolu (v aktuální úrovni boardu).
  const keKontroleCount = useMemo(
    () => (data?.tasks || []).filter((t) => t.uroven === effectiveBoard && t.ke_kontrole_kdy && !["hotovo", "archiv"].includes(t.status)).length,
    [data, effectiveBoard]
  );

  // Kolik úkolů filtr nechal (ukazuje se na počítači vedle hledání).
  const pocetCelkem = useMemo(
    () => (data?.tasks || []).filter((t) => t.uroven === effectiveBoard && !["hotovo", "archiv"].includes(t.status)).length,
    [data, effectiveBoard]
  );
  const pocetPoFiltru = useMemo(
    () => (data?.tasks || []).filter((t) => !["hotovo", "archiv"].includes(t.status)).filter(matchesFilter).length,
    [data, matchesFilter]
  );

  // Zapnuté filtry a jejich zrušení jedním tlačítkem.
  const filtrZapnuty = filterKategorie !== "vse" || filterDeadline !== "vse" || filterProjekty || aktivniPiloti.length > 0 || !!search.trim();
  const zrusitFiltry = () => {
    setFilterKategorie("vse");
    setFilterDeadline("vse");
    setFilterProjekty(false);
    setFilterPiloti([]);
    setSearch("");
  };

  // Seznam pro telefon: buď sloupec boardu, nebo pevná skupina.
  const VSECHNY_SEZNAMY = useMemo(() => [
    ...COLUMNS,
    { key: "kontrola", label: "Ke kontrole", ikona: Search, ton: "pozor", hint: "odevzdané ke schválení" },
    ...PINNED_GROUPS,
  ], []);
  const indexSeznamu = Math.max(0, VSECHNY_SEZNAMY.findIndex((x) => x.key === seznam));
  const aktivniSeznam = VSECHNY_SEZNAMY[indexSeznamu] || COLUMNS[3];
  const posunSeznam = (o) => {
    const dalsi = VSECHNY_SEZNAMY[indexSeznamu + o];
    if (dalsi) setSeznam(dalsi.key);
  };
  const telefonSeznam = useMemo(() => {
    if (seznam === "kontrola") {
      return (data?.tasks || [])
        .filter((t) => t.ke_kontrole_kdy && !["hotovo", "archiv"].includes(t.status))
        .filter(matchesFilter)
        .sort((a, b) => String(b.ke_kontrole_kdy).localeCompare(String(a.ke_kontrole_kdy)));
    }
    const arr = [...((data?.byStatus || {})[seznam] || [])].filter(matchesFilter);
    if (seznam === "hotovo" || seznam === "archiv") {
      return arr.sort((a, b) => String(b.hotovo_kdy || "").localeCompare(String(a.hotovo_kdy || "")));
    }
    return arr.sort((a, b) => {
      const pa = PRIO_ORDER[String(a.priority || "medium").toLowerCase()] ?? 2;
      const pb = PRIO_ORDER[String(b.priority || "medium").toLowerCase()] ?? 2;
      if (pa !== pb) return pa - pb;
      return String(a.deadline || "9999-12-31").localeCompare(String(b.deadline || "9999-12-31"));
    });
  }, [seznam, data, matchesFilter]);

  // Drag&drop mezi sloupci (HTML5 DnD, optimistic update).
  const changeStatus = useCallback(async (taskId, newStatus) => {
    let prevSnapshot = null;
    setData((prev) => {
      if (!prev) return prev;
      prevSnapshot = prev;
      const newBy = {};
      for (const k of Object.keys(prev.byStatus || {})) newBy[k] = [...(prev.byStatus[k] || [])];
      let moved = null;
      for (const k of Object.keys(newBy)) {
        const i = newBy[k].findIndex((t) => String(t.id) === String(taskId));
        if (i >= 0) { moved = { ...newBy[k][i], status: newStatus }; newBy[k].splice(i, 1); break; }
      }
      if (!moved) return prev;
      if (!newBy[newStatus]) newBy[newStatus] = [];
      newBy[newStatus].push(moved);
      return { ...prev, byStatus: newBy };
    });
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: taskId, status: newStatus }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Update selhal");
      // Kontrola: úkol se nepřesunul do Hotovo, ale odevzdal ke kontrole -
      // vrať optimistický přesun a řekni to člověku.
      if (j.keKontrole) {
        refetch();
        toast(j.message || "Úkol má štítek kontrola - odevzdáno zadavateli ke schválení, dostal zprávu ve Slacku.");
      }
    } catch (e) {
      if (prevSnapshot) setData(prevSnapshot);
      toast(`Chyba: ${e.message}`, "error");
    }
  }, [setData, refetch]);

  const doDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    setOpenTaskId(null);
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ action: "delete", id }) });
      if (!r.ok) throw new Error("Smazání selhalo");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
  }, [pendingDelete, refetch]);

  const pilots = data?.pilots || [];

  // S ?ukol=… je celá stránka detail úkolu. Kreslí se jako běžný obsah pod
  // horní lištou a řadou záložek, ne jako překryv přes celou obrazovku.
  if (ukolParam) {
    return (
      <>
        <TaskDetailModal taskId={ukolParam} people={people} fotoByName={fotoByName} tagSuggestions={tagSuggestions}
          kategorie={kategorie} canManage={canManage} canDelete={canDelete}
          allTasks={data?.tasks || []} onOpenTask={(id) => setSearchParams({ ukol: String(id) })}
          fullPage onChanged={refetch}
          onClose={() => {
            // Okno prohlížeče zavíráme jen když ho opravdu otevřela aplikace
            // (Ctrl + celá stránka). V záložce adminu se jen zavře detail.
            try { if (window.opener) window.close(); } catch (_) {}
            setSearchParams((sp) => { sp.delete("ukol"); return sp; });
          }}
          onDelete={(task) => setPendingDelete({ id: ukolParam, nazev: task?.nazev || "" })} />
        <ConfirmDialog
          open={!!pendingDelete}
          title="Smazat úkol?"
          description={pendingDelete ? `Úkol „${pendingDelete.nazev}" se smaže i s podúkoly a komentáři.` : ""}
          confirmLabel="Smazat"
          variant="danger"
          onCancel={() => setPendingDelete(null)}
          onConfirm={doDelete}
        />
      </>
    );
  }

  return (
    <Stranka sirka="plna">
      {/* 4.9.2026 (fáze 4a): gradientová hlavička s ikonovými akcemi. Data,
          endpointy ani chování se nemění, mění se jen podoba. */}
      <GradientHeader
        title="Úkoly"
        icon={ListChecks}
        loading={loading}
        akce={[
          { klic: "novy", ikona: Plus, popis: "Založit úkol nebo projekt", onClick: () => setVyberNoveho(true) },
        ]}
      />

      {error && (
        <Hlaska ton="problem" title="Úkoly se nepodařilo načíst" akce={<AkceZnovu onClick={refetch} />}>
          {String(error.message || error)}
        </Hlaska>
      )}

      {/* Osobní přehled (CSM/manager): co mám na stole a co čeká na moje rozhodnutí. */}
      {mojeRole !== "owner" && mojeJmeno && (() => {
        const vsechny = data?.tasks || [];
        const moje = vsechny.filter((t) => t.pilot === mojeJmeno && !["hotovo", "archiv"].includes(t.status));
        const poDeadlinu = moje.filter((t) => t.deadline && String(t.deadline).slice(0, 10) < today);
        const mojeJmenoLc = mojeJmeno.toLowerCase();
        const uzivJmenoLc = String(getCurrentUser()?.name || "").toLowerCase();
        const cekaNaMe = vsechny.filter((t) => t.ke_kontrole_kdy && [mojeJmenoLc, uzivJmenoLc].some((n) => n && String(t.zadavatel || "").toLowerCase() === n));
        const odevzdanoMnou = moje.filter((t) => t.ke_kontrole_kdy);
        return (
          <div className="glass flex flex-wrap items-center gap-2 !py-3">
            <span className="text-sm font-bold text-brand-deep demo-sensitive">Tvůj přehled</span>
            <Stitek ton="neutralni">{moje.length} otevřených úkolů</Stitek>
            {poDeadlinu.length > 0 && (
              <button type="button" onClick={() => { setFilterPiloti([mojeJmeno]); setFilterDeadline("po"); }}>
                <Stitek ton="problem">{poDeadlinu.length} po termínu</Stitek>
              </button>
            )}
            {odevzdanoMnou.length > 0 && <Stitek ton="pozor">{odevzdanoMnou.length} čeká na schválení zadavatelem</Stitek>}
            {cekaNaMe.length > 0 && <Stitek ton="pozor">{cekaNaMe.length} čeká na tvoji kontrolu</Stitek>}
            {moje.length === 0 && <span className="text-sm text-slate-400">Nic otevřeného, dobrá práce.</span>}
          </div>
        );
      })()}

      {/* --- Filtry: telefon (celoplošné řádky) ---------------------------- */}
      <div className="lg:hidden">
        <PruhFiltru zapnuto={filtrZapnuty} onZrusit={zrusitFiltry}>
          {canManage && (
            <Segment
              aria-label="Úroveň boardu"
              value={effectiveBoard}
              onChange={setBoard}
              items={[
                { key: "management", label: `Management (${countFor("management")})` },
                { key: "basic", label: `Basic (${countFor("basic")})` },
              ]}
            />
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledat úkol…" className="field" style={{ paddingLeft: "44px" }} />
          </div>
          <FiltrRadek
            stitek="Kategorie"
            vice={false}
            value={filterKategorie}
            onChange={setFilterKategorie}
            items={[{ key: "vse", label: "Všechny kategorie" }, ...kategorie.map((k) => ({ key: k, label: k }))]}
          />
          <FiltrRadek
            stitek="Piloti"
            popisVse="Všichni piloti"
            value={aktivniPiloti}
            onChange={(v) => setFilterPiloti(v)}
            items={pilots.map((jm) => ({ key: jm, label: jm === mojeJmeno ? `${jm} (já)` : jm }))}
          />
          <FiltrRadek
            stitek="Termín"
            vice={false}
            value={filterDeadline}
            onChange={setFilterDeadline}
            items={[
              { key: "vse", label: "Termín: vše" },
              { key: "tyden", label: "Tento týden (do čtvrtka)" },
              { key: "mesic", label: "Tento měsíc" },
              { key: "po", label: "Po termínu", ton: "problem" },
            ]}
          />
          {/* 4.9.2026: dřív to byl řádek jako ostatní filtry, ale nic nerozbaloval.
              Zapínátko je na první pohled zapínátko. */}
          <Zapinatko
            zapnuto={filterProjekty}
            onZmena={setFilterProjekty}
            popisek="Jen projekty"
            ikona={FolderKanban}
          />
          {canDelete && ukazkyCount > 0 && (
            <button type="button" onClick={() => setPendingDeleteUkazky(true)} className="btn btn-ghost btn-sm w-full"
              title="Smaže ukázkové úkoly z prvního spuštění (už se nevrátí)">
              <Trash2 className="h-4 w-4" /> Smazat ukázky ({ukazkyCount})
            </button>
          )}
        </PruhFiltru>
      </div>

      {/* --- Filtry: počítač (dva řádky, schváleno 4. 9. 2026) --------------
          Celoplošné řádky se na širokém monitoru roztáhly přes celou obrazovku.
          Nahoře board a filtry, dole hledání, počet a Zrušit vše. */}
      <div className="hidden lg:block">
        <div className="glass space-y-2.5 !p-3">
          <div className="flex items-center gap-3">
            {canManage && (
              <>
                <Segment
                  className="h-11 w-[270px] shrink-0"
                  aria-label="Úroveň boardu"
                  value={effectiveBoard}
                  onChange={setBoard}
                  items={[
                    { key: "management", label: `Management (${countFor("management")})` },
                    { key: "basic", label: `Basic (${countFor("basic")})` },
                  ]}
                />
                <span className="h-7 w-px shrink-0 bg-slate-200" aria-hidden="true" />
              </>
            )}
            <FiltrTlacitko
              className="flex-1"
              value={filterKategorie}
              onChange={setFilterKategorie}
              popisVse="Všechny kategorie"
              popis={filterKategorie === "vse" ? "Všechny kategorie" : filterKategorie}
              items={[{ key: "vse", label: "Všechny kategorie" }, ...kategorie.map((k) => ({ key: k, label: k }))]}
            />
            <FiltrTlacitko
              className="flex-1"
              vice
              value={aktivniPiloti}
              onChange={(v) => setFilterPiloti(v)}
              popisVse="Všichni piloti"
              popis={null}
              items={pilots.map((jm) => ({ key: jm, label: jm === mojeJmeno ? `${jm} (já)` : jm }))}
            />
            <FiltrTlacitko
              className="flex-1"
              value={filterDeadline}
              onChange={setFilterDeadline}
              popis={{ vse: "Termín: vše", tyden: "Tento týden", mesic: "Tento měsíc", po: "Po termínu" }[filterDeadline]}
              items={[
                { key: "vse", label: "Termín: vše" },
                { key: "tyden", label: "Tento týden (do čtvrtka)" },
                { key: "mesic", label: "Tento měsíc" },
                { key: "po", label: "Po termínu" },
              ]}
            />
            <button
              type="button"
              onClick={() => setFilterProjekty((v) => !v)}
              title="Zobrazit jen projekty (úkoly s navázanými úkoly)"
              className={`flex h-11 shrink-0 items-center gap-2 rounded-xl px-3.5 text-[15px] ${
                filterProjekty ? "border-2 border-brand bg-brand-50 font-bold text-brand-deep" : "border border-slate-300 bg-white font-semibold text-slate-700"
              }`}
            >
              <FolderKanban className={`h-5 w-5 ${filterProjekty ? "" : "text-slate-400"}`} /> Jen projekty
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledat úkol podle názvu, popisu nebo pilota…"
                className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-11 pr-3 text-[15px] focus:border-brand focus:outline-none" />
            </div>
            <span className="shrink-0 text-sm text-slate-500">
              Zobrazeno <b className="tabular-nums text-slate-800">{pocetPoFiltru}</b> z {pocetCelkem}
            </span>
            <button type="button" onClick={zrusitFiltry} disabled={!filtrZapnuty}
              className="flex h-11 shrink-0 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3.5 text-[15px] font-semibold text-slate-700 disabled:opacity-40">
              <X className="h-4 w-4 text-slate-400" /> Zrušit vše
            </button>
            {canDelete && ukazkyCount > 0 && (
              <button type="button" onClick={() => setPendingDeleteUkazky(true)} className="btn btn-ghost btn-sm shrink-0"
                title="Smaže ukázkové úkoly z prvního spuštění (už se nevrátí)">
                <Trash2 className="h-4 w-4" /> Ukázky ({ukazkyCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Telefon a tablet: jeden seznam ---------------------------------
          Kanban se sem nevešel a rolovalo se do strany naslepo. Listování je
          stejné jako u období v time trackingu (schváleno 4. 9. 2026): šipka,
          název seznamu, šipka. Klepnutí na název otevře celý výběr, kde jsou
          i pevné skupiny (Ke kontrole, Hotovo, Archiv). */}
      <div className="lg:hidden">
        <div className="mb-3 flex items-stretch gap-2">
          <button type="button" className="btn-mesic" onClick={() => posunSeznam(-1)} disabled={indexSeznamu <= 0}
            aria-label="Předchozí seznam" title="Předchozí seznam">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setVyberOtevren(true)}
            className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-brand/20 bg-white px-3 shadow-glass"
          >
            {aktivniSeznam.ikona && <aktivniSeznam.ikona className="h-5 w-5 shrink-0 text-brand" />}
            <span className="truncate text-base font-bold text-slate-900">{aktivniSeznam.label}</span>
            <span className="shrink-0 rounded-full bg-brand-100 px-2 py-0.5 text-sm font-bold tabular-nums text-brand-deep">{telefonSeznam.length}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
          <button type="button" className="btn-mesic" onClick={() => posunSeznam(1)} disabled={indexSeznamu >= VSECHNY_SEZNAMY.length - 1}
            aria-label="Další seznam" title="Další seznam">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {loading && !data ? (
          <div className="glass !p-0"><SkeletonRows rows={5} /></div>
        ) : telefonSeznam.length === 0 ? (
          <div className="glass">
            <EmptyState icon={ListChecks} title="V tomto seznamu nic není" hint="Zkus jiný seznam nebo zrušit filtry." />
          </div>
        ) : (
          <div className="space-y-2">
            {telefonSeznam.map((t) => (
              <TaskCard key={t.id} task={t} today={today} onOpen={setOpenTaskId} foto={fotoByName[t.pilot]} />
            ))}
          </div>
        )}
        <VyberSeznamu
          open={vyberOtevren}
          onClose={() => setVyberOtevren(false)}
          title="Který seznam"
          ikona={Columns3}
          value={seznam}
          onChange={setSeznam}
          skupiny={[
            {
              nadpis: "Board",
              polozky: COLUMNS.map((c) => ({
                key: c.key,
                label: c.label,
                popis: c.hint,
                ikona: c.ikona,
                ton: c.ton,
                pocet: sortTasks(byStatus[c.key]).length,
              })),
            },
            {
              nadpis: "Ostatní",
              polozky: [
                { key: "kontrola", label: "Ke kontrole", popis: "odevzdané ke schválení", ikona: Search, ton: "pozor", pocet: keKontroleCount },
                ...PINNED_GROUPS.map((g) => ({
                  key: g.key,
                  label: g.label,
                  ikona: g.ikona,
                  ton: g.ton,
                  pocet: sortTasks(byStatus[g.key]).length,
                })),
              ],
            },
          ]}
        />
      </div>

      {/* --- Počítač: kanban a pevné skupiny jako dřív ---------------------- */}
      <div className="hidden lg:block lg:space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setOpenGroup(openGroup === "kontrola" ? null : "kontrola")}
            title="Úkoly odevzdané ke kontrole, schvaluje zadavatel"
            className={`btn btn-sm ${openGroup === "kontrola" ? "btn-primary" : "btn-secondary"}`}>
            <Search className="h-4 w-4" /> Ke kontrole
            <Stitek velikost="mala" ton={keKontroleCount > 0 ? "pozor" : "neutralni"}>{keKontroleCount}</Stitek>
          </button>
          {PINNED_GROUPS.map((g) => {
            const list = sortTasks(byStatus[g.key]);
            const GIkona = g.ikona;
            return (
              <button key={g.key} type="button" onClick={() => setOpenGroup(openGroup === g.key ? null : g.key)}
                className={`btn btn-sm ${openGroup === g.key ? "btn-primary" : "btn-secondary"}`}>
                <GIkona className="h-4 w-4" /> {g.label}
                <Stitek velikost="mala" ton="neutralni">{list.length}</Stitek>
              </button>
            );
          })}
        </div>

        {openGroup && (
          <KartaSeznam
            ikona={openGroup === "kontrola" ? Search : (PINNED_GROUPS.find((g) => g.key === openGroup)?.ikona || Archive)}
            title={openGroup === "kontrola" ? "Ke kontrole" : (PINNED_GROUPS.find((g) => g.key === openGroup)?.label || "")}
            podtitulek={openGroup === "kontrola"
              ? "Odevzdané úkoly, schválí je zadavatel v detailu, nebo vrátí s komentářem."
              : openGroup === "hotovo" ? "Dokončené úkoly zůstávají tady." : "Úkoly, které ztratily relevanci."}
            odznak={groupList.length}
          >
            <div className="p-3">
              <input value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} placeholder="Hledat jméno nebo úkol…" className="field mb-2" />
              {groupList.length === 0 ? (
                <EmptyState icon={Archive} title="Nic tu není" />
              ) : (
                <div className="divide-y divide-slate-100">
                  {groupList.map((t) => {
                    const prio = PRIORITY_META[t.priority] || PRIORITY_META.Medium;
                    const jeProj = t.je_projekt === true || (t.deti_total || 0) > 0;
                    return (
                      <button key={t.id} type="button" onClick={() => setOpenTaskId(t.id)}
                        className="flex min-h-[60px] w-full items-center gap-3 px-1 py-2 text-left">
                        <Avatar name={t.pilot} foto={fotoByName[t.pilot] || ""} className="h-8 w-8 shrink-0" textClassName="text-[11px]" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 truncate text-sm font-semibold text-slate-800">
                            {jeProj && <FolderKanban className="h-3.5 w-3.5 shrink-0 text-brand" />}
                            {t.nazev}
                          </span>
                          <span className="mt-1 flex flex-wrap gap-1.5">
                            {t.kategorie && <Stitek velikost="mala" ton="neutralni">{t.kategorie}</Stitek>}
                            <Stitek velikost="mala" ton={PRIORITA_TON[t.priority] || "neutralni"}>{prio.label}</Stitek>
                            {t.zadavatel && <Stitek velikost="mala" ton="neutralni">zadal {t.zadavatel}</Stitek>}
                            {t.deadline && <Stitek velikost="mala" ton="neutralni">termín {Number(t.deadline.slice(8, 10))}. {Number(t.deadline.slice(5, 7))}.</Stitek>}
                            {openGroup === "kontrola"
                              ? <Stitek velikost="mala" ton="pozor">kontroluje {(t.zadavatel || "owner").split(" ")[0]}</Stitek>
                              : t.hotovo_kdy && <Stitek velikost="mala" ton={openGroup === "hotovo" ? "ok" : "neutralni"}>
                                  {Number(t.hotovo_kdy.slice(8, 10))}. {Number(t.hotovo_kdy.slice(5, 7))}. {t.hotovo_kdy.slice(0, 4)}
                                </Stitek>}
                          </span>
                        </span>
                        <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-400" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </KartaSeznam>
        )}

        {/* Kanban — 5 sloupců dle Notionu, přetahování mezi nimi zůstává. */}
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
          {COLUMNS.map((col) => {
            const list = sortTasks(byStatus[col.key]);
            const t = tonSloupce(col.ton);
            const CIkona = col.ikona;
            return (
              <div key={col.key} className="flex min-h-[280px] w-[280px] min-w-[260px] flex-1 shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glass"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData("text/task-id");
                  if (id) changeStatus(id, col.key);
                }}>
                <div className={`flex items-center gap-2 px-3 py-2 ${t.pole}`}>
                  <CIkona className={`h-4 w-4 shrink-0 ${t.text}`} />
                  <span className={`min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-wider ${t.text}`}>{col.label}</span>
                  <span className={`shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums ${t.text}`}>{list.length}</span>
                </div>
                <div className="space-y-2 p-2.5">
                  {(rozbaleneSloupce[col.key] ? list : list.slice(0, 10)).map((t2) => (
                    <div key={t2.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/task-id", t2.id)}>
                      <TaskCard task={t2} today={today} onOpen={setOpenTaskId} foto={fotoByName[t2.pilot]} />
                    </div>
                  ))}
                  {list.length > 10 && (
                    <button type="button" onClick={() => setRozbaleneSloupce((r) => ({ ...r, [col.key]: !r[col.key] }))}
                      className="btn btn-secondary btn-sm w-full">
                      {rozbaleneSloupce[col.key] ? "Sbalit (nechat prvních 10)" : `Zobrazit dalších ${list.length - 10}`}
                    </button>
                  )}
                  {list.length === 0 && !loading && <div className="py-6 text-center text-xs text-slate-400">Prázdné</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {canManage && (
        <RutinyPanel rutiny={data?.rutiny || []} people={people} fotoByName={fotoByName} kategorie={kategorie} onChanged={refetch} isOwner={isOwner && mojeRole === "owner"} sPotvrzenim={!!data?.rutinySPotvrzenim && mojeRole === "csm"}
          treneri={data?.treneriRutin || []} trenerSPotvrzenim={!!data?.rutinyTrenerSPotvrzenim && ["manager", "csm"].includes(mojeRole)} />
      )}
      {/* Slack agent Joky: VÝHRADNĚ owner — i v "Náhledu jako" jiná role se skryje. */}
      {isOwner && mojeRole === "owner" && <SlackAgentPanel />}

      <VyberSeznamu
        open={vyberNoveho}
        onClose={() => setVyberNoveho(false)}
        title="Co chceš založit"
        ikona={Plus}
        onChange={(k) => setShowAdd({ jeProjekt: k === "projekt" })}
        skupiny={[{
          polozky: [
            { key: "ukol", label: "Nový úkol", popis: "Jedna věc, která má pilota a termín.", ikona: ListChecks, ton: "znacka" },
            { key: "projekt", label: "Nový projekt", popis: "Sdružuje další úkoly (max 20) a ukazuje jejich plnění.", ikona: FolderKanban, ton: "znacka" },
          ],
        }]}
      />

      {showAdd && (
        <AddTaskModal people={people} tagSuggestions={tagSuggestions} kategorie={kategorie} canManage={canManage} defaultUroven={effectiveBoard}
          defaultJeProjekt={showAdd.jeProjekt === true} existingTasks={data?.tasks || []}
          onClose={() => setShowAdd(false)} onCreated={refetch} />
      )}
      {openTaskId && (
        <TaskDetailModal taskId={openTaskId} people={people} fotoByName={fotoByName} tagSuggestions={tagSuggestions}
          kategorie={kategorie} canManage={canManage} canDelete={canDelete}
          allTasks={data?.tasks || []} onOpenTask={setOpenTaskId}
          onClose={() => setOpenTaskId(null)} onChanged={refetch}
          onDelete={(task) => setPendingDelete({ id: openTaskId, nazev: task?.nazev || "" })} />
      )}
      <ConfirmDialog
        open={pendingDeleteUkazky}
        title="Smazat ukázkové úkoly?"
        description={`Smaže se ${ukazkyCount} ukázkových úkolů z prvního spuštění. Znovu se už nevytvoří.`}
        confirmLabel="Smazat ukázky"
        variant="danger"
        onConfirm={doDeleteUkazky}
        onCancel={() => setPendingDeleteUkazky(false)}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        title="Smazat úkol?"
        description={pendingDelete ? `Úkol "${pendingDelete.nazev}" se nenávratně smaže včetně podúkolů a komentářů.` : ""}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={doDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stranka>
  );
}

